/*!
 * Slimmy × Kaching Bundles — purchase guard
 * ------------------------------------------------------------------
 * Purpose
 *   Make sure NOTHING can be added to the cart from the PDP or the
 *   homepage until Kaching Bundles has really rendered its deal block
 *   AND written the selected offer into the product form. Prevents the
 *   "clicked at page load → 1 bottle @ $39.99 instead of the 3-pack" bug.
 *
 * How readiness is detected (no fixed delays)
 *   Evidence: Kaching's storefront bundle (extension assets
 *   kaching-bundles.js / kaching-bundles-block.js) does, in order:
 *     1. customElements.define('kaching-bundles-block', …)  (light DOM)
 *     2. replaces the children of <kaching-bundle product-id> with a
 *        <kaching-bundles-block> and sets data-initialized="true"
 *        (or "skipped" when no deal applies to the product/market)
 *     3. on every 'items-changed' event of the block it calls
 *        AddToCartForm.updateItem(item), which writes into the form:
 *          [name="id"]                         = item.id
 *          [name="quantity"]                   = item.quantity (creates a
 *                                                hidden input if needed)
 *          [name="properties[__kaching_bundles]"] = deal JSON (only for
 *                                                bars that carry a deal)
 *     4. sets window.kachingBundlesInitialized = true and dispatches
 *        window 'kaching-bundles-initialized' once all placeholders ran.
 *   READY ⇔ customElements.get('kaching-bundles-block') is defined
 *         ∧ placeholder[data-initialized="true"] contains the block
 *         ∧ block has a rendered `.kaching-bundles__bar--selected`
 *         ∧ block.items() (Kaching public element method) is non-empty
 *         ∧ the form's id / quantity inputs equal the selected item
 *         ∧ properties[__kaching_bundles] is present when the item has one.
 *   This is re-evaluated synchronously at click time, so even a click
 *   that lands mid-update is blocked.
 *
 * What is guarded
 *   - The form's submit button (Kaching reuses it as "its" button).
 *   - External triggers: buttons with [form="<form id>"] (Shrine sticky ATC)
 *     and [data-slimmy-kaching-trigger="<form id>"] (homepage sticky bar).
 *     These never submit on their own; they are routed through the Kaching
 *     button (button.click()) so Kaching's own click handler runs.
 *   - Kaching's own sticky bar (kaching-bundles-sticky-atc) and dynamic
 *     checkout buttons inside the form.
 *   - Any submit event of the guarded form.
 *
 * Duplicate-click lock
 *   After an accepted click the context is "in flight": further trusted
 *   clicks are swallowed. Kaching's own programmatic re-click
 *   (button.click() ~200 ms later, isTrusted=false) is let through.
 *   Unlock on: Shrine product-form finishing (button loses .loading),
 *   cart pub/sub 'cart-update', page navigation (pageshow restore), no
 *   cart activity at all within 4 s (Kaching aborted), or 15 s safety.
 *
 * Markup contract (rendered by Liquid)
 *   <form data-slimmy-kaching-form="pdp|home" data-slimmy-single-compare="5999">
 *     <kaching-bundle product-id="…"></kaching-bundle>
 *     <button type="submit" data-slimmy-kaching-submit disabled
 *             data-slimmy-kguard="loading" …>
 *
 * Labels (v2)
 *   The visible label text is swapped for real (not a CSS overlay): the label
 *   element is [data-slimmy-kguard-text] (server-rendered "Loading offers…"
 *   with the original in data-slimmy-kguard-orig), else .main-atc__label__text /
 *   .sticky-atc__label__text / .button__label / strong, else the button's own
 *   text nodes get wrapped once. Restored to the original when ready.
 *
 * Sticky bars (v2)
 *   Sticky bars bound to a guarded form ([data-slimmy-sticky-for="<form id>"]
 *   or the <sticky-atc> that owns a [form=<id>] button) get
 *   data-slimmy-sticky="show" only while the form's submit button has scrolled
 *   ABOVE the viewport and the offer root (#offer / [data-slimmy-offer-root] /
 *   the form) is not intersecting; otherwise "hide" (CSS forces it off-screen).
 *   --slimmy-sticky-h is published on <html> for bottom padding.
 *
 * Subscriptions / extensibility (v2)
 *   If Kaching's selected item carries a selling plan key (selling_plan /
 *   sellingPlanId / sellingPlan), READY additionally requires the form's
 *   selling_plan control (hidden, select, or checked radio — including
 *   controls associated via form="<id>") to equal it ('' when none).
 *   Extra project checks: window.SlimmyKachingGuardConfig.extraChecks =
 *   [function (form, item, block) { return 'reason' | null; }]
 *
 * Pack variants + subscriptions (v3)
 *   Each Kaching bar = quantity 1 of a different pack variant (1 bottle /
 *   3-bottle pack / 5-bottle pack). READY is now keyed on the VARIANT id:
 *   the form's [name=id] must equal the selected Kaching item's id AND be a
 *   variant of this product (pack map rendered by
 *   snippets/slimmy-purchase-extras.liquid as
 *   <script type="application/json" data-slimmy-pack-map>). Quantity is only
 *   a consistency check (form quantity == item quantity); a pack variant
 *   (bottles > 1) with quantity > CFG.maxPackQty (default 1) is refused
 *   because it would ship 9/25 bottles (Kaching bar misconfigured).
 *   selling_plan: when the item carries a plan, the form must carry the same
 *   plan and (if the variant has allocations) the plan must be allocated to
 *   that variant. When the item carries NO plan, a leftover non-empty
 *   selling_plan in the form also blocks (CFG.strictSellingPlan, default on).
 *   Subscription panel / sticky meta are driven from the same evaluation.
 *
 * Subscriptions off (v8, theme setting "Allow Subscribe & save" = off, preorder period)
 *   The pack map carries "subsOff": true, empty "plans" and "subPlanIds" (Kaching Subscriptions plan ids).
 *   enforceOneTime(): if Kaching's Subscribe tab is selected, click Kaching's OWN One-time tab (Kaching state
 *   stays consistent); an item carrying a selling plan is never READY (click/submit blocked until one-time);
 *   subscription selling_plan controls in Slimmy forms are disabled on submit; /cart/add(.js) fetch/XHR bodies
 *   lose a selling_plan that is one of subPlanIds (STOQ's own Preorder plan is never touched). Offer cards,
 *   sticky and panel always read one-time. CSS (.slimmy-subs-off on the slot) hides the toggle.
 *   Turning the setting back on restores the normal subscription behaviour (no code change).
 *
 * Preorder prices (v9, theme setting "Show preorder discount prices", display only)
 *   preorderDiscount.mode "fixed": each preorder pack in the pack map carries poOff {ot, sub} (cents, from
 *   settings.slimmy_preorder_fixed_amounts = the Shopify automatic fixed discounts); one-time shows price - poOff.ot,
 *   subscription first shipment price - poOff.sub. Percent mode unchanged. Nothing is written into the form/cart.
 *   Totals on the buttons/sticky are written with a leading empty comment node so Kaching's addToCartButton.updatePrice()
 *   (immediately + ~2 s after every selection) cannot find and overwrite them with its undiscounted pricing().
 *
 * Pack-fixed prices + Kaching variant integrity (v10)
 *   Offer-card prices (price, strike, Save, per bottle) and the one-time button/sticky totals are computed ONLY
 *   from the pack map (Liquid: variant price, compare-at, poOff) of the card's OWN variant (Kaching deal config
 *   defaultVariantsV2 by data-deal-bar-id, else bar position) — never from Kaching's rendered text or pricing(),
 *   which Kaching re-prices itself. Idempotent: every pass writes the same values.
 *   The button/sticky follow the HIGHLIGHTED card. evaluate() is not ready while the highlighted card's own variant
 *   differs from the variant Kaching would add; if that persists (Kaching re-pointed its bars at one variant)
 *   the purchase is blocked with the standard Retry, which reloads the page.
 *   Root cause guarded: with ?variant= in the URL, Kaching treats ANY later history change (pushState /
 *   replaceState / popstate, e.g. an in-page anchor) as a native variant change and, on this single-option
 *   product, re-points every deal bar at that variant. These pages have no variant picker (Kaching's preselected
 *   deal decides), so the stale ?variant= is removed from the address bar once at boot (stripVariantParam).
 *
 * Double-tap hardening (v11)
 *   One purchase tap = exactly one add, also for fast double taps and script clicks:
 *   - While an add is in flight only ONE programmatic (isTrusted=false) click on the Kaching button is let through
 *     (Kaching's own follow-up re-click). The guard's own routing click for sticky triggers is exempt (_routing).
 *     Further programmatic clicks are swallowed.
 *   - Only ONE form submit per in-flight add reaches the theme product-form.
 *   - After the add ends (product-form finished / cartUpdate / safety) a cooldown (CFG.cooldownMs, 1200 ms) swallows
 *     purchase clicks and submits, so a click landing between /cart/add returning and the unlock cannot add again.
 *   - SlimmyKachingGuard.busy([form]) exposes in-flight/cooldown for proxy buttons (slimmy-pdp.js Pick-your-pack).
 *   Nothing about WHAT is added changes (variant, quantity, selling_plan, properties).
 *
 * Preorder add through fetch (v12)
 *   A guarded form whose submit would go out as a NATIVE POST to /cart/add (no theme product-form took it, e.g. the
 *   homepage .sm-kform) bypasses STOQ's fetch/XHR hijack, so the cart line arrives without STOQ's preorder properties
 *   until STOQ's /cart sweep patches it after load. When such a submit is allowed by the guard, subscriptions are off
 *   (pack map subsOff) and the submitted variant is a preorder (pack map po:true), the guard sends the SAME FormData
 *   (form + submitter) with fetch('/cart/add.js') instead: STOQ's hijack then adds exactly what it adds on the PDP
 *   (it waits up to 2 s for STOQ to arm when STOQ's hijack is enabled for the page), STOQ's cart-attribute flush is
 *   awaited (max 1.5 s, like STOQ's own ajaxFormSubmit), then the page goes to return_to or /cart like the native POST.
 *   If STOQ did not tag the line, STOQ's own updateCartSellingPlans() runs before leaving (max 2.5 s).
 *   Error response → message under the button, flight ends; network failure → native submit (STOQ's fallback).
 *   Forms already handled by a product-form (PDP) are untouched (their submit is defaultPrevented first).
 *
 * Selected card stays selected on re-tap (v13)
 *   Kaching's quantity-break bar treats a click on the ALREADY-selected card (its .kaching-bundles__bar-main,
 *   also reached via Enter/Space) as "deselect": onDealBarDeselect re-selects the first quantity-1 bar, which in
 *   this deal is "Buy 1" (all bars are quantity 1, different pack variants). So the first tap on the preselected
 *   3-pack, or a second tap on any chosen card, silently switched the offer to 1 bottle. Such a click on the
 *   selected card of a guarded form is stopped in capture phase before Kaching sees it; the card stays chosen.
 *   Taps on other cards, and controls inside a card, are untouched. Nothing about WHAT is added changes.
 *
 * Fast-tap highlight ↔ Kaching sync (v14)
 *   v13 decided "is this the selected card?" from the DOM `.kaching-bundles__bar--selected` class. During fast
 *   pack taps that class can disagree with Kaching's own deal (selectedDealBarId / items() / form id) for a
 *   moment, so the final tap was swallowed as a re-tap while Kaching stayed on the previous pack — highlight
 *   and add-variant diverged, evaluate() stayed on kaching-variant-mismatch, and after mismatchMs the CTA
 *   flipped to "Offers unavailable". v14: (1) re-tap detection uses Kaching's state (selectedDealBarId, else
 *   items()/form variant vs the card's configured variant), falling back to the DOM class only if those are
 *   unavailable; (2) while mismatched and no add is in flight, self-heal once by programmatically selecting
 *   the highlighted card so Kaching catches up to the last tap; (3) soft mismatch stays "Updating offer…"
 *   (purchase still blocked) instead of failing the offer UI — fail-closed only at click/submit, or after
 *   exhausted heals on a hard remap mismatch. Double-tap / preorder / re-tap→Buy-1 guards unchanged.
 *
 * Debug
 *   Any of: theme setting "Slimmy · Kaching → debug", ?slimmy_debug=1,
 *   localStorage.slimmyKachingDebug = '1', or window.SlimmyKachingGuard.debug(true)
 *   Inspect: window.SlimmyKachingGuard.contexts / .evaluateAll()
 */
(function () {
  'use strict';
  if (window.SlimmyKachingGuard) return;

  var USER_CFG = window.SlimmyKachingGuardConfig || {};
  var CFG = {
    timeoutMs: USER_CFG.timeoutMs || 10000,      // no readiness within → failed + Retry
    inflightMaxMs: USER_CFG.inflightMaxMs || 15000, // hard unlock safety
    noActivityMs: 4000,                          // unlock if nothing happened after click
    cooldownMs: USER_CFG.cooldownMs || 1200,     // v11: swallow purchase clicks/submits this long after an add ends
    extraChecks: USER_CFG.extraChecks || [],     // fn(form, item, block) → reason string | null
    maxPackQty: USER_CFG.maxPackQty || 1,        // max quantity of a multi-bottle pack variant per add
    strictSellingPlan: USER_CFG.strictSellingPlan !== false,
    mismatchMs: USER_CFG.mismatchMs || 2500,     // v10: highlighted card ≠ Kaching item for this long → failed
    healGapMs: USER_CFG.healGapMs || 350,        // v14: min gap between mismatch self-heals
    healMax: USER_CFG.healMax || 4,              // v14: max self-heals per mismatch streak
    // v4: NO hard-coded prices. Panel values come from Kaching's rendered bars / pricing() and
    // Shopify variant + selling-plan data (pack map). Card decoration can be disabled:
    decoratePanel: USER_CFG.decoratePanel !== false,
    labels: {
      loading: 'Loading offers\u2026',
      syncing: 'Updating offer\u2026',
      failed: 'Offers unavailable',
      failedMsg: 'We couldn\u2019t load the bundle offers.',
      retry: 'Retry',
      fallback: 'View offers on the product page'
    }
  };

  var DEBUG = !!USER_CFG.debug;
  try {
    if (/[?&]slimmy_debug=1/.test(location.search)) DEBUG = true;
    if (window.localStorage && localStorage.getItem('slimmyKachingDebug') === '1') DEBUG = true;
  } catch (e) {}
  function log() {
    if (!DEBUG) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('%c[slimmy-kaching]', 'color:#B34E68;font-weight:bold');
    console.log.apply(console, a);
  }

  var FORM_SEL = 'form[data-slimmy-kaching-form]:not([data-slimmy-kaching-form=""])';
  var PLACEHOLDER_SEL = 'kaching-bundle, kaching-bundle-deals';
  var BLOCK_TAG = 'kaching-bundles-block';
  var contexts = [];

  /* ---------------------------------------------------------------- utils */
  function now() { return Date.now(); }
  function moneyCents(text) {
    if (!text) return null;
    var digits = String(text).replace(/[^\d]/g, '');
    return digits ? parseInt(digits, 10) : null;
  }
  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }
  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(sel)) return el;
      el = el.parentElement || (el.getRootNode && el.getRootNode().host) || null;
    }
    return null;
  }

  /* ------------------------------------------------------------- context */
  function Ctx(form) {
    this.form = form;
    this.kind = form.getAttribute('data-slimmy-kaching-form');
    this.button = form.querySelector('[data-slimmy-kaching-submit]') ||
      form.querySelector('button[type="submit"], input[type="submit"]');
    this.originallyUnavailable = !!(this.button && this.button.getAttribute('data-unavailable') === 'true');
    this.singleCompare = parseInt(form.getAttribute('data-slimmy-single-compare') || '0', 10) || 0;
    this.fallbackUrl = form.getAttribute('data-slimmy-kaching-fallback-url') || '';
    this.state = 'loading';
    this.reason = '';
    this.deadline = 0;
    this.inflight = false;
    this.inflightStarted = 0;
    this.sawCartActivity = false;
    this.cooldownUntil = 0;   // v11
    this._progClicks = 0;     // v11: programmatic clicks let through in the current flight
    this._submits = 0;        // v11: form submits let through in the current flight
    this._routing = false;    // v11: true only during the guard's own external→main routing click
    this.retries = 0;
    this.statusEl = null;
    this.boundBlock = null;
    this._timers = {};
    // Kaching may have finished its init pass before this deferred script ran
    this.initSeenAt = window.kachingBundlesInitialized ? now() : 0;
  }
  Ctx.prototype.placeholder = function () {
    return this.form.querySelector(PLACEHOLDER_SEL);
  };
  Ctx.prototype.block = function () {
    var ph = this.placeholder();
    return ph ? ph.querySelector(BLOCK_TAG) : null;
  };
  // NB: never use form.id — Shopify product forms contain <input name="id">,
  // which shadows the form's .id property. Always read the attribute.
  Ctx.prototype.formId = function () { return this.form.getAttribute('id') || ''; };
  Ctx.prototype.externalTriggers = function () {
    var id = this.formId(), out = [];
    if (id) {
      var a = document.querySelectorAll('[form="' + id + '"][type="submit"], [data-slimmy-kaching-trigger="' + id + '"]');
      for (var i = 0; i < a.length; i++) if (!this.form.contains(a[i])) out.push(a[i]);
    }
    return out;
  };
  // Pack map (variant id → {bottles,title,price,plans:{planId:{price,name,options}},fallback})
  Ctx.prototype.packMap = function () {
    if (this._packMap !== undefined) return this._packMap;
    var el = this.form.querySelector('script[data-slimmy-pack-map]');
    var fid = this.formId();
    if (!el && fid) el = document.querySelector('script[data-slimmy-pack-map][data-for="' + fid + '"]');
    var map = null;
    var full = null;
    if (el) { try { full = JSON.parse(el.textContent); map = full.variants || null; } catch (e) { log('pack map parse error', e); } }
    this._po = full && full.preorderDiscount || null;
    this._subsOff = !!(full && full.subsOff === true);
    this._subPlanIds = (full && full.subPlanIds || []).map(function (x) { return String(x).split('/').pop(); });
    this._packMap = map;
    // data-slimmy-single-compare is the SELECTED variant's compare-at (Liquid). On a ?variant=<pack> link that is a pack's
    // compare-at (e.g. $119.97), which must stay visible as the pack's strike: only a single-bottle compare is hidden.
    if (map && this.singleCompare) {
      for (var k in map) if (map[k] && (map[k].bottles || 1) > 1 && Number(map[k].compareCents) === this.singleCompare) { this.singleCompare = 0; break; }
    }
    return map;
  };
  Ctx.prototype.subsOff = function () { this.packMap(); return !!this._subsOff; };
  Ctx.prototype.pack = function (variantId) {
    var m = this.packMap();
    return m ? (m[String(variantId)] || null) : null;
  };
  Ctx.prototype.quantityInput = function () {
    var q = this.form.querySelector('[name="quantity"]');
    var fid = this.formId();
    if (!q && fid) q = document.querySelector('[name="quantity"][form="' + fid + '"]');
    return q;
  };

  /* ---------------------------------------------------------- evaluation */
  // Returns {ready:bool, fail:bool, why:string, item, block}
  function evaluate(ctx) {
    var r = { ready: false, fail: false, why: '' };
    if (!window.customElements || !customElements.get(BLOCK_TAG)) { r.why = 'kaching-bundles-block not defined yet'; return r; }
    var ph = ctx.placeholder();
    if (!ph) { r.why = 'no <kaching-bundle> placeholder in form'; r.fail = !!window.kachingBundlesInitialized; return r; }
    if (ph.dataset.initialized === 'skipped') { r.why = 'Kaching skipped placeholder (no deal for product/market)'; r.fail = true; return r; }
    var block = ph.querySelector(BLOCK_TAG);
    if (ph.dataset.initialized !== 'true' || !block) {
      r.why = 'placeholder not initialized';
      // Kaching finished its init pass but did not render ours → fail fast
      if (window.kachingBundlesInitialized && ctx.initSeenAt && now() - ctx.initSeenAt > 1500) r.fail = true;
      return r;
    }
    r.block = block;
    if (!block.querySelector('.kaching-bundles__bar--selected')) { r.why = 'no selected deal bar rendered'; return r; }
    var items = null;
    try { items = typeof block.items === 'function' ? block.items() : null; } catch (e) { items = null; }
    if (!items || !items.length) { r.why = 'block.items() empty'; return r; }
    var idInput = ctx.form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) { r.why = 'no variant id input'; return r; }
    var item = null;
    for (var i = 0; i < items.length; i++) if (String(items[i].id) === String(idInput.value)) { item = items[i]; break; }
    if (!item) { r.why = 'form id ' + idInput.value + ' not in Kaching items yet'; return r; }
    // v3: the selected bar is identified by its VARIANT (pack), not by quantity 3/5
    var pack = ctx.pack(item.id);
    if (ctx.packMap() && !pack) { r.why = 'variant ' + item.id + ' is not a variant of this product'; return r; }
    // v10: the highlighted card's own variant must be the one Kaching adds (see header "variant integrity")
    var selBar = selectedDealBar(block), barVid = selBar ? barDefaultVariant(block, selBar) : null;
    if (barVid && String(barVid) !== String(item.id)) {
      if (!ctx._mmSince) { ctx._mmSince = now(); clearTimeout(ctx._timers.mm); ctx._timers.mm = setTimeout(function () { tick(ctx); }, CFG.mismatchMs + 60); }
      r.why = 'kaching-variant-mismatch: highlighted card is variant ' + barVid + ' but Kaching would add ' + item.id;
      // v14: soft mismatch (highlight ≠ Kaching deal) → self-heal; stay !ready (Updating offer…) so purchase is
      // fail-closed at click/submit without flipping to Offers unavailable. Hard mismatch (Kaching bar id already
      // matches the highlight but the variant was remapped) keeps the v10 failed path after mismatchMs.
      if (!ctx.inflight) {
        if (!isKachingSelectedBar(block, selBar, ctx.form)) maybeHealMismatch(ctx, block, selBar);
        else if (now() - ctx._mmSince > CFG.mismatchMs) r.fail = true;
      }
      return r;
    }
    ctx._mmSince = 0;
    ctx._healCount = 0;
    var q = ctx.quantityInput();
    if (!q || Number(q.value) !== Number(item.quantity)) {
      r.why = 'quantity input (' + (q ? q.value : 'missing') + ') != Kaching item quantity ' + item.quantity; return r;
    }
    if (pack && pack.bottles > 1 && Number(item.quantity) > CFG.maxPackQty) {
      r.why = 'pack variant ' + item.id + ' (' + pack.bottles + ' bottles) with quantity ' + item.quantity + ' — Kaching bar must use quantity 1'; return r;
    }
    r.pack = pack;
    if (ctx.subsOff()) {
      if (expectedPlan(item)) { enforceOneTime(ctx); r.why = 'subscriptions off: Subscribe selected, switching Kaching to one-time'; return r; }
      disableSubPlanControls(ctx);
    }
    var hasDeal = item.properties && item.properties.__kaching_bundles;
    if (hasDeal) {
      var p = ctx.form.querySelector('[name="properties[__kaching_bundles]"]');
      if (!p || !p.value) { r.why = 'properties[__kaching_bundles] not written yet'; return r; }
    }
    var sp = sellingPlanMismatch(ctx, item, pack);
    if (sp) { r.why = sp; return r; }
    for (var x = 0; x < CFG.extraChecks.length; x++) {
      var why = null;
      try { why = CFG.extraChecks[x](ctx.form, item, block); } catch (e) { why = 'extraCheck threw: ' + e.message; }
      if (why) { r.why = why; return r; }
    }
    r.ready = true; r.item = item; r.plan = expectedPlan(item);
    r.why = 'ready (variant ' + item.id + (pack ? ' "' + pack.title + '"' : '') + ' ×' + item.quantity +
      (r.plan ? ', selling_plan ' + r.plan : ', one-time') + (hasDeal ? ', deal property set' : ', no deal property') + ')';
    return r;
  }

  /* ------------------------------------------------------ selling plans */
  // Ready for subscriptions: only enforced when Kaching's item exposes a plan key.
  function hasPlanKey(item) { return item && ('selling_plan' in item || 'sellingPlanId' in item || 'sellingPlan' in item); }
  function expectedPlan(item) {
    var v = item.selling_plan != null ? item.selling_plan : item.sellingPlanId != null ? item.sellingPlanId : (item.sellingPlan && (item.sellingPlan.id || item.sellingPlan));
    return v == null || v === '' ? '' : String(v).split('/').pop();
  }
  function actualPlan(ctx) {
    var els = ctx.form.elements, val = '';
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.name !== 'selling_plan' || el.disabled) continue;
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
      if (el.value) { val = String(el.value).split('/').pop(); break; }
    }
    return val;
  }
  // Preorder pass: the plan dropdown is hidden (frequency follows the pack). If Kaching ever selects a plan that is not
  // one of the pack's subscription plans (e.g. STOQ's "Preorder" plan), switch its hidden select back to the pack's plan.
  function fixPlan(ctx, item, pack) {
    var exp = expectedPlan(item);
    if (!exp || !pack || !pack.plans || !Object.keys(pack.plans).length || pack.plans[exp]) return false;
    var now = Date.now();
    if (ctx._planFixAt && now - ctx._planFixAt < 600) return false;
    var root = null;
    try { root = ctx.block(); } catch (e) {}
    root = root || ctx.form;
    var sels = root.querySelectorAll('select.kaching-bundles__selling-plan-select, .kaching-bundles__bar-selling-plan select, .kaching-bundles__subscriptions select');
    for (var i = 0; i < sels.length; i++) {
      var sel = sels[i];
      for (var j = 0; j < sel.options.length; j++) {
        var o = sel.options[j], v = o.__value != null ? o.__value : o.value;
        var id = v && typeof v === 'object' ? (v.id || v.gid || '') : v;
        id = String(id || '').split('/').pop();
        var name = (o.textContent || '').replace(/\s+/g, ' ').trim();
        var hit = pack.plans[id] ? true : false;
        if (!hit) for (var k in pack.plans) if (pack.plans[k].name && pack.plans[k].name === name) { hit = true; break; }
        if (!hit) continue;
        if (sel.selectedIndex !== j) {
          ctx._planFixAt = now;
          sel.selectedIndex = j;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          sel.dispatchEvent(new Event('input', { bubbles: true }));
          log('plan', 'switched hidden plan select from', exp, 'to', id || name);
        }
        return true;
      }
    }
    return false;
  }
  function sellingPlanMismatch(ctx, item, pack) {
    fixPlan(ctx, item, pack);
    var act = actualPlan(ctx);
    if (!hasPlanKey(item) || !expectedPlan(item)) {
      // one-time selected: a leftover subscription plan in the form would turn it into a subscription
      return CFG.strictSellingPlan && act ? 'selling_plan ' + act + ' still in form but one-time selected' : null;
    }
    var exp = expectedPlan(item);
    if (exp !== act) return 'selling_plan (' + (act || 'none') + ') != selected (' + exp + ')';
    if (pack && pack.plans && Object.keys(pack.plans).length && !pack.plans[exp]) {
      return 'selling_plan ' + exp + ' is not allocated to variant ' + item.id;
    }
    return null;
  }

  /* ------------------------------------------- subscriptions off (v8) */
  // Kaching's own purchase toggle tabs (One-time / Subscribe). Uses isSubscribeTab (defined below, hoisted).
  function purchaseTabs(ctx) {
    var b = ctx.block(); if (!b) return [];
    var out = [];
    each(b.querySelectorAll('.kaching-bundles__subscriptions .kaching-bundles__bar'), function (t) {
      if (closest(t.parentElement, '.kaching-bundles__subscriptions .kaching-bundles__bar')) return; // nested
      out.push(t);
    });
    return out;
  }
  // If Subscribe is selected while subscriptions are off, select Kaching's One-time tab through Kaching itself.
  function enforceOneTime(ctx) {
    if (!ctx.subsOff()) return false;
    var tabs = purchaseTabs(ctx), selSub = null, ot = null;
    tabs.forEach(function (t) {
      var sub = isSubscribeTab(t);
      if (sub === true && t.classList.contains('kaching-bundles__bar--selected')) selSub = t;
      if (sub === false && !ot) ot = t;
    });
    var live = null;
    try { var it = ctx.block() && ctx.block().items(); live = it && it[0]; } catch (e) {}
    var needs = !!selSub || !!(live && expectedPlan(live));
    if (!needs || !ot) return false;
    var t = now();
    if (ctx._otFixAt && t - ctx._otFixAt < 600) return true;
    ctx._otFixAt = t;
    var target = ot.querySelector('input[type="radio"]') || ot.querySelector('label') || ot.querySelector('.kaching-bundles__bar-container') || ot;
    try { target.click(); } catch (e) {}
    log('subs-off', 'Subscribe was selected → clicked Kaching One-time tab');
    return true;
  }
  function isSubPlanId(ctx, v) {
    v = String(v || '').split('/').pop();
    return !!v && ctx._subPlanIds && ctx._subPlanIds.indexOf(v) !== -1;
  }
  // Disable subscription selling_plan controls in (or associated with) the guarded form. STOQ's Preorder plan is left alone.
  function disableSubPlanControls(ctx) {
    if (!ctx.subsOff()) return;
    var els = ctx.form.elements;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.name !== 'selling_plan' || el.disabled || !el.value) continue;
      if (isSubPlanId(ctx, el.value)) { el.disabled = true; el.setAttribute('data-slimmy-subs-off', ''); log('subs-off', 'disabled form selling_plan', el.value); }
    }
  }
  // Network layer: strip Kaching Subscriptions plan ids from /cart/add(.js) bodies (fetch + XHR). Installed only when a
  // pack map says subsOff. Never adds anything, never touches other plans or other fields.
  var SUBS_OFF_PLANS = null;
  function subsOffPlans() {
    if (SUBS_OFF_PLANS) return SUBS_OFF_PLANS;
    var set = [];
    each(document.querySelectorAll('script[data-slimmy-pack-map]'), function (el) {
      try { var m = JSON.parse(el.textContent); if (m && m.subsOff === true) (m.subPlanIds || []).forEach(function (x) { x = String(x).split('/').pop(); if (set.indexOf(x) === -1) set.push(x); }); } catch (e) {}
    });
    SUBS_OFF_PLANS = set;
    return set;
  }
  function anySubsOffMap() {
    var on = false;
    each(document.querySelectorAll('script[data-slimmy-pack-map]'), function (el) { try { if (JSON.parse(el.textContent).subsOff === true) on = true; } catch (e) {} });
    return on;
  }
  function isAddUrl(u) { return /\/cart\/add(\.js)?(\?|#|$)/.test(String(u || '')); }
  function isBlockedPlan(v) { v = String(v == null ? '' : v).split('/').pop(); return !!v && subsOffPlans().indexOf(v) !== -1; }
  function stripParams(p) { // URLSearchParams | FormData
    var hit = false, keys = [];
    p.forEach(function (v, k) { if ((k === 'selling_plan' || /^items\[\d+\]\[selling_plan\]$/.test(k)) && isBlockedPlan(v)) keys.push(k); });
    keys.forEach(function (k) { p.delete(k); hit = true; });
    return hit;
  }
  function stripObj(o) {
    var hit = false;
    function one(it) { if (!it || typeof it !== 'object') return; ['selling_plan', 'sellingPlan', 'sellingPlanId'].forEach(function (k) { if (k in it && isBlockedPlan(it[k] && it[k].id || it[k])) { delete it[k]; hit = true; } }); }
    if (o && Array.isArray(o.items)) o.items.forEach(one); else one(o);
    return hit;
  }
  function sanitizeBody(body) {
    if (body == null) return { body: body, hit: false };
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) return { body: body, hit: stripParams(body) };
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return { body: body, hit: stripParams(body) };
      if (typeof body === 'string') {
        var t = body.trim();
        if (t.charAt(0) === '{') { var o = JSON.parse(t); if (stripObj(o)) return { body: JSON.stringify(o), hit: true }; return { body: body, hit: false }; }
        var p = new URLSearchParams(body);
        if (stripParams(p)) return { body: p.toString(), hit: true };
      }
    } catch (e) { log('subs-off sanitize error', e); }
    return { body: body, hit: false };
  }
  function sanitizeUrl(u) {
    try {
      var url = new URL(String(u), location.href), sp = url.searchParams.get('selling_plan');
      if (sp != null && isBlockedPlan(sp)) { url.searchParams.delete('selling_plan'); return { url: url.toString(), hit: true }; }
    } catch (e) {}
    return { url: u, hit: false };
  }
  var NET_GUARD = false;
  function installSubsOffNetGuard() {
    if (NET_GUARD || !anySubsOffMap()) return;
    NET_GUARD = true;
    if (typeof window.fetch === 'function') {
      var of = window.fetch;
      window.fetch = function (input, init) {
        try {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          if (isAddUrl(url)) {
            if (typeof input === 'string') { var su = sanitizeUrl(input); if (su.hit) { input = su.url; log('subs-off', 'stripped selling_plan from fetch URL'); } }
            if (init && 'body' in init) { var sb = sanitizeBody(init.body); if (sb.hit) { init = Object.assign({}, init, { body: sb.body }); log('subs-off', 'stripped selling_plan from fetch body'); } }
          }
        } catch (e) { log('subs-off fetch guard error', e); }
        return of.call(this, input, init);
      };
    }
    if (window.XMLHttpRequest) {
      var XP = XMLHttpRequest.prototype, oo = XP.open, os = XP.send;
      XP.open = function (m, u) {
        try { this._slimmyAdd = isAddUrl(u); if (this._slimmyAdd) { var su = sanitizeUrl(u); if (su.hit) { arguments[1] = su.url; log('subs-off', 'stripped selling_plan from XHR URL'); } } } catch (e) {}
        return oo.apply(this, arguments);
      };
      XP.send = function (body) {
        try { if (this._slimmyAdd) { var sb = sanitizeBody(body); if (sb.hit) { body = sb.body; log('subs-off', 'stripped selling_plan from XHR body'); } } } catch (e) {}
        return os.call(this, body);
      };
    }
    log('subs-off', 'network guard installed; plans', subsOffPlans());
  }

  /* --------------------------------------------------------------- labels */
  var OUR_LABELS = null;
  function isOurLabel(t) {
    if (!OUR_LABELS) OUR_LABELS = [CFG.labels.loading, CFG.labels.syncing, CFG.labels.failed];
    return OUR_LABELS.indexOf(String(t).trim()) !== -1;
  }
  function labelEl(btn) {
    if (btn._slimmyLabelEl && btn.contains(btn._slimmyLabelEl)) return btn._slimmyLabelEl;
    var el = btn.querySelector('[data-slimmy-kguard-text]') ||
      btn.querySelector('.main-atc__label__text, .sticky-atc__label__text, .button__label, strong');
    if (!el) {
      // first element child that has letters and is not a price
      for (var i = 0; i < btn.children.length; i++) {
        var c = btn.children[i];
        if (/[A-Za-z]{3}/.test(c.textContent) && !/\d/.test(c.textContent)) { el = c; break; }
      }
    }
    if (!el) {
      // wrap the button's own direct text nodes once
      var txt = [];
      for (var n = btn.firstChild; n; n = n.nextSibling) if (n.nodeType === 3 && n.nodeValue.trim()) txt.push(n);
      if (txt.length) {
        el = document.createElement('span');
        el.textContent = txt.map(function (t) { return t.nodeValue.trim(); }).join(' ');
        btn.insertBefore(el, txt[0]);
        txt.forEach(function (t) { t.parentNode.removeChild(t); });
      }
    }
    if (el) {
      el.setAttribute('data-slimmy-kguard-text', '');
      if (!el.hasAttribute('data-slimmy-kguard-orig') && !isOurLabel(el.textContent)) {
        el.setAttribute('data-slimmy-kguard-orig', el.textContent.replace(/\s+/g, ' ').trim());
      }
    }
    btn._slimmyLabelEl = el;
    return el;
  }
  function origLabel(btn) {
    var el = labelEl(btn);
    return el ? (el.getAttribute('data-slimmy-kguard-orig') || '') : '';
  }
  function setLabel(btn, text) {
    var el = labelEl(btn);
    if (!el) return;
    var want = text != null ? text : (btn._slimmyReadyLabel || origLabel(btn));
    if (want && el.textContent !== want) el.textContent = want;
  }

  /* ------------------------------------------------------------------ UI */
  function setButtonState(btn, state, label, disable) {
    if (!btn) return;
    var swap = state === 'loading' || state === 'syncing' || state === 'failed';
    setLabel(btn, swap ? label : null);
    if (state) {
      btn.setAttribute('data-slimmy-kguard', state);
      btn.setAttribute('data-slimmy-kguard-label', label || '');
      btn.setAttribute('aria-busy', state === 'loading' || state === 'syncing' || state === 'busy' ? 'true' : 'false');
    } else {
      btn.removeAttribute('data-slimmy-kguard');
      btn.removeAttribute('data-slimmy-kguard-label');
      btn.removeAttribute('aria-busy');
    }
    if (disable === true) btn.disabled = true;
    else if (disable === false) btn.disabled = false;
  }

  function ensureStatus(ctx) {
    if (ctx.statusEl && ctx.statusEl.isConnected) return ctx.statusEl;
    var el = document.createElement('div');
    el.className = 'slimmy-kguard-status';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.hidden = true;
    el.innerHTML = '<span class="slimmy-kguard-status__msg"></span>' +
      '<button type="button" class="slimmy-kguard-retry" data-slimmy-kguard-retry>' + CFG.labels.retry + '</button>' +
      (ctx.fallbackUrl ? '<a class="slimmy-kguard-fallback" href="' + ctx.fallbackUrl + '">' + CFG.labels.fallback + '</a>' : '');
    el.querySelector('[data-slimmy-kguard-retry]').addEventListener('click', function (e) {
      e.preventDefault(); retry(ctx);
    });
    var anchor = ctx.button && ctx.button.parentElement ? ctx.button : ctx.form.lastElementChild;
    if (anchor && anchor.parentElement) anchor.parentElement.insertBefore(el, anchor.nextSibling);
    else ctx.form.appendChild(el);
    ctx.statusEl = el;
    return el;
  }

  function render(ctx) {
    var s = ctx.state, L = CFG.labels;
    var mainLabel = s === 'failed' ? L.failed : s === 'syncing' ? L.syncing : L.loading;
    if (s === 'ready') {
      setButtonState(ctx.button, ctx.inflight ? 'busy' : null, '', ctx.originallyUnavailable ? true : (ctx.inflight ? undefined : false));
    } else {
      setButtonState(ctx.button, s, mainLabel, true);
    }
    ctx.externalTriggers().forEach(function (b) {
      if (s === 'ready') setButtonState(b, ctx.inflight ? 'busy' : null, '', ctx.inflight ? true : ctx.originallyUnavailable);
      else setButtonState(b, s, mainLabel, s !== 'failed'); // failed: stays clickable → scrolls to Retry
    });
    var st = ensureStatus(ctx);
    if (s === 'failed') {
      st.hidden = false;
      each(st.querySelectorAll('[data-slimmy-kguard-retry], .slimmy-kguard-fallback'), function (el) { el.hidden = false; }); // v12
      st.querySelector('.slimmy-kguard-status__msg').textContent = L.failedMsg;
    } else if (ctx._addErr && !ctx.inflight && now() < ctx._addErr.until) { // v12: last add's error stays visible
      st.hidden = false;
      st.querySelector('.slimmy-kguard-status__msg').textContent = ctx._addErr.msg;
    } else {
      st.hidden = true;
    }
  }

  function setState(ctx, s, why) {
    if (ctx.state !== s) log('state', ctx.kind, ctx.state, '→', s, why || '');
    ctx.state = s; ctx.reason = why || '';
    render(ctx);
    if (s === 'ready') cosmetics(ctx);
  }

  /* --------------------------------------------------- cosmetics / sync */
  // (a) only one "Most popular" badge on the page
  // (b) hide a single-bottle strikethrough equal to the unconfirmed
  //     product compare-at (e.g. $59.99). Bundle totals are untouched.
  // (c) mirror the selected Kaching offer into sticky bars.
  function cosmetics(ctx) {
    var badges = document.querySelectorAll('.kaching-bundles__bar-most-popular');
    var visibleSeen = false;
    for (var i = 0; i < badges.length; i++) {
      if (!visibleSeen) { visibleSeen = true; badges[i].removeAttribute('data-slimmy-hide'); continue; }
      badges[i].setAttribute('data-slimmy-hide', 'dup-badge');
    }
    var block = ctx.block();
    if (!block) return;
    ctx.packMap();
    if (ctx.singleCompare) {
      var fulls = block.querySelectorAll('.kaching-bundles__bar-full-price');
      for (var j = 0; j < fulls.length; j++) {
        if (moneyCents(fulls[j].textContent) === ctx.singleCompare) fulls[j].setAttribute('data-slimmy-hide', 'single-compare');
        else if (fulls[j].getAttribute('data-slimmy-hide') === 'single-compare') fulls[j].removeAttribute('data-slimmy-hide');
      }
    }
    syncStickies(ctx, block);
  }

  // Purchase-mode aware offer summary for the selected item (v3)
  function intervalFrom(plan) {
    if (!plan) return '';
    var txt = [plan.name || ''].concat(plan.options || []).join(' ');
    var m = /(\d+)\s*-?\s*(day|week|month|year)s?/i.exec(txt);
    if (m) { var n = parseInt(m[1], 10), u = m[2].toLowerCase(); return n === 1 ? u : n + ' ' + u + 's'; }
    m = /\b(daily|weekly|monthly|yearly|annually)\b/i.exec(txt);
    if (m) return { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year', annually: 'year' }[m[1].toLowerCase()];
    m = /\b(day|week|month|year)\b/i.exec(txt);
    return m ? m[1].toLowerCase() : '';
  }
  function offerSummary(ctx, block, ev) {
    var pricing = null;
    try { pricing = block.pricing(); } catch (e) {}
    var item = ev.item, pack = ev.pack || ctx.pack(item.id), planId = ctx.subsOff() ? '' : (ev.plan || '');
    var qty = Number(item.quantity) || 1;
    var bottles = (pack && pack.bottles ? pack.bottles : 1) * qty;
    var kTotal = pricing && pricing.discountedPrice && pricing.discountedPrice.formatted;
    var kFull = pricing && pricing.fullPrice && pricing.fullPrice.formatted;
    var out = { bottles: bottles, subscription: !!planId, planId: planId, total: kTotal || '', full: kFull || '', interval: '', pack: pack };
    if (planId) {
      var plan = pack && pack.plans ? pack.plans[planId] : null;
      out.interval = intervalFrom(plan);            // Shopify selling plan delivery interval
      out.planName = plan && plan.name || '';
      // per-delivery price: Kaching's live price first; Shopify allocation price (×1) only if Kaching gave none
      out.planPrice = kTotal || (plan && qty === 1 ? plan.price : '');
      // renewal price for the disclosure: the selling plan's own allocation price for this pack (what renews)
      out.renewPrice = plan && plan.price ? (qty === 1 ? plan.price : '') : '';
      if (!out.total) out.total = out.planPrice;
    }
    return out;
  }

  /* ---------------------------------------------- v4 panel decoration */
  // Presentation only: never selects anything. Reads Kaching's rendered bar prices.
  function centsOf(text) { var m = /(\d[\d,]*)(?:[.,](\d{2}))?(?!\d)/.exec(String(text || '').replace(/\s/g, '')); if (!m) return null; return parseInt(m[1].replace(/,/g, ''), 10) * 100 + (m[2] ? parseInt(m[2], 10) : 0); }
  function fmtLike(sample, cents) {
    var v = (cents / 100).toFixed(2);
    var t = String(sample || '').trim();
    if (/\d,\d{2}(?!\d)/.test(t) && !/\d\.\d{2}(?!\d)/.test(t)) v = v.replace('.', ',');
    return /[\d.,]+/.test(t) ? t.replace(/\d[\d.,]*/, v) : v;
  }
  /* Preorder discount display layer (theme setting "Show preorder discount prices", default OFF).
     Display only: the real discount must exist in Shopify. Applies to preorder packs (map flag "po") only.
     Cents: discount = price × pct, rounded down (Shopify percentage discounts) or to the nearest cent (setting). */
  function poCfg(ctx) { ctx.packMap(); var c = ctx._po; return c && c.on && (c.mode === 'fixed' || Number(c.pct) > 0) ? c : null; }
  // sub = the price is a subscription (first shipment) price. Fixed mode: exact cents from the pack map (poOff), else unchanged.
  function poCents(ctx, pack, cents, sub) {
    var c = poCfg(ctx);
    if (!c || !pack || pack.po !== true || cents == null) return cents;
    if (c.mode === 'fixed') {
      var off = pack.poOff ? Number(sub ? pack.poOff.sub : pack.poOff.ot) || 0 : 0;
      return off > 0 && off < cents ? cents - off : cents;
    }
    var raw = cents * Number(c.pct) / 100;
    var d = c.round === 'nearest' ? Math.round(raw) : Math.floor(raw + 1e-7);
    return cents - d;
  }
  function poText(ctx, pack, text, sub) {
    var c = centsOf(text); if (c == null) return text;
    var d = poCents(ctx, pack, c, sub);
    return d === c ? text : fmtLike(text, d);
  }
  // total/full pair for buttons + sticky: total discounted; strike = the larger of Kaching's compare and the regular total
  function poTotals(ctx, pack, total, full, sub) {
    if (!total || !poCfg(ctx) || !pack || pack.po !== true) return { total: total, full: full };
    var tc = centsOf(total), fc = full ? centsOf(full) : null;
    if (tc == null) return { total: total, full: full };
    var strike = fc != null && fc > tc && fc !== ctx.singleCompare ? full : total;
    return { total: fmtLike(total, poCents(ctx, pack, tc, sub)), full: strike };
  }
  // Button/sticky price nodes: write only on change, behind a leading empty comment (see header "Preorder prices").
  // Element.textContent ignores the comment, so every reader sees just the price.
  function setPriceText(el, text) {
    text = text || '';
    var f = el.firstChild;
    if (el.textContent === text && (!text || (f && f.nodeType === 8))) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    if (text) { el.appendChild(document.createComment('')); el.appendChild(document.createTextNode(text)); }
  }
  // v10: one-time price of a pack from the pack map only (variant price, compare-at, poOff) — never from DOM text.
  // strike = compare-at when above the price, else the regular price when a preorder discount applies.
  function packOneTime(ctx, pack) {
    if (!pack || pack.priceCents == null || !pack.price) return null;
    var pc = Number(pack.priceCents), cc = Number(pack.compareCents) || 0;
    if (!(pc > 0)) return null;
    var dpc = poCents(ctx, pack, pc, false);
    var strike = cc > pc ? cc : pc;
    if (!(strike > dpc)) strike = 0;
    return { cents: dpc, strikeCents: strike, sample: pack.price, total: fmtLike(pack.price, dpc), full: strike ? fmtLike(pack.price, strike) : '' };
  }
  function ensureEl(parent, key, tag, text, before) {
    if (!parent) return null;
    var el = parent.querySelector(':scope > [data-slimmy-inj="' + key + '"]');
    if (!text) { if (el) el.remove(); return null; }
    if (!el) { el = document.createElement(tag); el.setAttribute('data-slimmy-inj', key); el.className = 'slimmy-k-' + key; if (before && before.parentNode === parent) parent.insertBefore(el, before); else parent.appendChild(el); }
    if (el.textContent !== text) el.textContent = text;
    return el;
  }
  // v10: a card's OWN variant from Kaching's deal config (defaultVariantsV2 by data-deal-bar-id). Never from the
  // selected state / block.items(): Kaching can re-point bars at another variant (see header).
  function barDefaultVariant(block, bar) {
    var id = bar && bar.getAttribute('data-deal-bar-id'), db = null;
    if (!id) return null;
    try { db = block.dealBlock; } catch (e) {}
    var dv = db && db.defaultVariantsV2;
    if (dv) for (var i = 0; i < dv.length; i++) if (dv[i] && dv[i].dealBarId === id && dv[i].variantGIDs && dv[i].variantGIDs[0]) return String(dv[i].variantGIDs[0]).split('/').pop();
    return null;
  }
  function barVariantId(block, bar, idx, map) {
    var v = barDefaultVariant(block, bar);
    if (v) return v;
    // fallback: Nth deal bar → Nth variant by position (fixed order, not the selected state)
    if (idx == null || idx < 0) {
      idx = -1;
      var sibs = bar.parentNode ? bar.parentNode.children : [], n = 0;
      for (var j = 0; j < sibs.length; j++) { if (!sibs[j].classList || !sibs[j].classList.contains('kaching-bundles__bar')) continue; if (sibs[j] === bar) { idx = n; break; } n++; }
    }
    var keys = Object.keys(map || {}).sort(function (a, b) { return (map[a].position || 0) - (map[b].position || 0); });
    return idx >= 0 ? (keys[idx] || null) : null;
  }
  function barQty(block, bar) {
    var id = bar.getAttribute('data-deal-bar-id'), db = null;
    try { db = block.dealBlock; } catch (e) {}
    var bars = db && db.dealBars;
    if (bars) for (var i = 0; i < bars.length; i++) if (bars[i].id === id) return Number(bars[i].quantity) || 1;
    return 1;
  }
  function deliveryText(pack, planId, sub) {
    if (!sub) return 'One-time delivery';
    var plans = pack && pack.plans || {}, plan = plans[planId] || plans[Object.keys(plans)[0]];
    var iv = intervalFrom(plan);
    return iv ? 'Delivered every ' + iv : (plan && plan.name) || 'Subscription delivery';
  }
  function decoratePanel(ctx, block, ev) {
    if (!CFG.decoratePanel || !block) return;
    var ph = ctx.placeholder(); if (!ph) return;
    var map = ctx.packMap() || {};
    var sub = !ctx.subsOff() && !!(ev && ev.plan);
    var mode = sub ? 'subscribe' : 'one-time';
    if (ph.getAttribute('data-slimmy-mode') !== mode) ph.setAttribute('data-slimmy-mode', mode);
    if (!ph.hasAttribute('data-slimmy-panel')) ph.setAttribute('data-slimmy-panel', 'v4');
    var root = block.querySelector('.kaching-bundles__block') || block;
    each(root.querySelectorAll('.kaching-bundles__block-title'), function (t) {
      var nx = t.nextElementSibling, role = nx && nx.classList.contains('kaching-bundles__subscriptions-wrapper') ? 'purchase-label' : 'block-title';
      if (t.getAttribute('data-slimmy-role') !== role) t.setAttribute('data-slimmy-role', role);
    });
    var barsWrap = null;
    each(root.querySelectorAll('.kaching-bundles__bars'), function (b) { if (!barsWrap && !b.classList.contains('kaching-bundles__subscriptions') && !closest(b, '.kaching-bundles__subscriptions-wrapper')) barsWrap = b; });
    if (!barsWrap) return;
    var prev = barsWrap.previousElementSibling;
    if (!(prev && prev.hasAttribute('data-slimmy-offer-label'))) {
      var lab = document.createElement('div'); lab.className = 'slimmy-k-offerlabel'; lab.setAttribute('data-slimmy-offer-label', '');
      lab.textContent = 'Choose your offer'; barsWrap.parentNode.insertBefore(lab, barsWrap);
    }
    var bars = [];
    each(barsWrap.children, function (c) { if (c.classList && c.classList.contains('kaching-bundles__bar')) bars.push(c); });
    bars.forEach(function (bar, idx) {
      var vid = barVariantId(block, bar, idx, map), pack = vid ? map[vid] : null;
      var bottles = (pack && pack.bottles ? pack.bottles : 1) * barQty(block, bar);
      var priceEl = bar.querySelector('.kaching-bundles__bar-price:not([data-slimmy-inj])');
      var fullEl = bar.querySelector('.kaching-bundles__bar-pricing .kaching-bundles__bar-full-price:not([data-slimmy-inj])');
      var pTxt = priceEl ? priceEl.textContent.trim() : '';
      var pc = centsOf(pTxt), fc = fullEl ? centsOf(fullEl.textContent) : null;
      var save = pc != null && fc != null && fc > pc ? fc - pc : 0;
      var unit = '';
      var pricing = bar.querySelector('.kaching-bundles__bar-pricing');
      // v10: one-time cards are priced from the card's own pack (pack map), never from Kaching's text
      var fx = !sub && poCfg(ctx) && pack && pack.po === true ? packOneTime(ctx, pack) : null;
      var poOn = !!(poCfg(ctx) && pack && pack.po === true && (fx || pc != null) && priceEl && pricing);
      if (poOn && fx) {
        var pe = ensureEl(priceEl.parentNode, 'poprice', 'div', fx.total, priceEl.nextSibling);
        if (pe && pe.className !== 'kaching-bundles__bar-price slimmy-k-poprice') pe.className = 'kaching-bundles__bar-price slimmy-k-poprice';
        var fe = ensureEl(priceEl.parentNode, 'pofull', 'div', fx.full, pe ? pe.nextSibling : null);
        if (fe && fe.className !== 'kaching-bundles__bar-full-price slimmy-k-pofull') fe.className = 'kaching-bundles__bar-full-price slimmy-k-pofull';
        if (!bar.hasAttribute('data-slimmy-po-price')) bar.setAttribute('data-slimmy-po-price', '');
        if (!bar.hasAttribute('data-slimmy-po-fixed')) bar.setAttribute('data-slimmy-po-fixed', ''); // CSS hides Kaching's own strike
        save = fx.strikeCents ? fx.strikeCents - fx.cents : 0;
        pc = fx.cents; pTxt = fx.sample;
      } else if (poOn) {
        // subscription mode (subscriptions are off in the preorder period): legacy, from Kaching's own price node
        if (bar.hasAttribute('data-slimmy-po-fixed')) bar.removeAttribute('data-slimmy-po-fixed');
        var isSingle = fc != null && ctx.singleCompare && fc === ctx.singleCompare;
        if (isSingle && fullEl && fullEl.getAttribute('data-slimmy-hide') !== 'single-compare') fullEl.setAttribute('data-slimmy-hide', 'single-compare');
        var fcVis = fc != null && fc > pc && !isSingle;
        var strikeC = fcVis ? fc : pc, dpc = poCents(ctx, pack, pc, sub);
        var pe2 = ensureEl(priceEl.parentNode, 'poprice', 'div', fmtLike(pTxt, dpc), priceEl.nextSibling);
        if (pe2 && pe2.className !== 'kaching-bundles__bar-price slimmy-k-poprice') pe2.className = 'kaching-bundles__bar-price slimmy-k-poprice';
        var fe2 = ensureEl(priceEl.parentNode, 'pofull', 'div', fcVis ? '' : pTxt, pe2 ? pe2.nextSibling : null);
        if (fe2 && fe2.className !== 'kaching-bundles__bar-full-price slimmy-k-pofull') fe2.className = 'kaching-bundles__bar-full-price slimmy-k-pofull';
        if (!bar.hasAttribute('data-slimmy-po-price')) bar.setAttribute('data-slimmy-po-price', '');
        save = strikeC > dpc ? strikeC - dpc : 0;
        pc = dpc;
      } else {
        if (priceEl) { ensureEl(priceEl.parentNode, 'poprice', 'div', ''); ensureEl(priceEl.parentNode, 'pofull', 'div', ''); }
        if (bar.hasAttribute('data-slimmy-po-price')) bar.removeAttribute('data-slimmy-po-price');
        if (bar.hasAttribute('data-slimmy-po-fixed')) bar.removeAttribute('data-slimmy-po-fixed');
      }
      if (pc != null && bottles > 0) {
        var exact = pc % bottles === 0, u = Math.round(pc / bottles);
        unit = (exact ? '' : '\u2248') + fmtLike(pTxt, u) + ' per bottle';
      }
      var left = bar.querySelector('.kaching-bundles__bar-content-left');
      var second = bar.querySelector('.kaching-bundles__bar-second-line') || left;
      // freeship 2026-10-11: savings rounded to whole dollars ("Save $40" / "Save $80"); 1-bottle card stays plain
      var saveR = save && bottles > 1 ? Math.round(save / 100) * 100 : 0;
      ensureEl(second, 'save', 'span', saveR ? 'Save ' + fmtLike(pTxt, saveR).replace(/[.,]00(?!\d)/, '') : '');
      ensureEl(left, 'delivery', 'div', deliveryText(pack, ev && ev.plan, sub));
      ensureEl(bar.querySelector('.kaching-bundles__bar-pricing'), 'unit', 'div', unit);
      // thumbnail (only when Kaching renders no bar image)
      var main = bar.querySelector('.kaching-bundles__bar-main');
      var hasK = !!bar.querySelector('.kaching-bundles__bar-image');
      var th = main && main.querySelector(':scope > [data-slimmy-inj="thumb"]');
      if (main && !hasK && pack && pack.thumb) {
        if (!th) { th = document.createElement('span'); th.setAttribute('data-slimmy-inj', 'thumb'); th.className = 'slimmy-k-thumb'; th.innerHTML = '<img alt="" loading="lazy" width="56" height="56">'; main.insertBefore(th, main.querySelector('.kaching-bundles__bar-content')); }
        var img = th.querySelector('img'); if (img.getAttribute('src') !== pack.thumb) img.setAttribute('src', pack.thumb);
        th.setAttribute('data-bottles', bottles);
      } else if (th) th.remove();
      // "Most popular": our small rectangular badge, text taken from Kaching's badge
      var kb = bar.querySelector('.kaching-bundles__bar-most-popular');
      var cont = bar.querySelector('.kaching-bundles__bar-container');
      // freeship 2026-10-11: 5-bottle card (no Kaching badge) gets "Best Value" in the same badge style
      var bdg = ensureEl(cont, 'badge', 'span', kb ? (kb.textContent.replace(/\s+/g, ' ').trim() || 'Most popular') : (bottles >= 5 ? 'Best Value' : ''));
      var bv = !kb && bottles >= 5;
      if (bv !== bar.hasAttribute('data-slimmy-bestvalue')) { if (bv) bar.setAttribute('data-slimmy-bestvalue', ''); else bar.removeAttribute('data-slimmy-bestvalue'); }
    });
    // purchase toggle prices (both halves show the selected pack's price in that mode)
    var selPack = ev && ev.pack;
    if (ctx.subsOff()) return; // toggle hidden while subscriptions are off: no Subscribe price decoration
    each(root.querySelectorAll('.kaching-bundles__subscriptions .kaching-bundles__bar'), function (tab) {
      var tp = tab.querySelector('.kaching-bundles__bar-price:not([data-slimmy-inj])');
      if (!tp) return;
      var on = !!(poCfg(ctx) && selPack && selPack.po === true);
      var tTxt = tp.textContent.trim();
      var te = ensureEl(tp.parentNode, 'poprice', 'div', on && centsOf(tTxt) != null ? poText(ctx, selPack, tTxt, isSubscribeTab(tab) === true) : '', tp.nextSibling);
      if (te && te.className !== 'kaching-bundles__bar-price slimmy-k-poprice') te.className = 'kaching-bundles__bar-price slimmy-k-poprice';
      if (te && !tab.hasAttribute('data-slimmy-po-price')) tab.setAttribute('data-slimmy-po-price', '');
      else if (!te && tab.hasAttribute('data-slimmy-po-price')) tab.removeAttribute('data-slimmy-po-price');
    });
  }

  // v6: the displayed offer (totals, compare, meta, panel) always follows Kaching's LIVE selection — block.items() +
  // block.pricing() — not the form. The form may still be catching up (Kaching writes it after its re-render), which used
  // to leave the old total on the buttons for a moment. Read-only: never selects or changes anything in Kaching.
  function liveEv(ctx, block) {
    var items = null;
    try { items = typeof block.items === 'function' ? block.items() : null; } catch (e) { items = null; }
    var it = items && items[0];
    if (!it) return null;
    return { item: it, pack: ctx.pack(it.id), plan: expectedPlan(it) };
  }
  // v10: pack of the HIGHLIGHTED card (its own variant), else Kaching's item
  function displayPack(ctx, block, ev) {
    var bar = selectedDealBar(block), vid = bar ? barVariantId(block, bar, -1, ctx.packMap() || {}) : null;
    return (vid && ctx.pack(vid)) || (ev && ev.pack) || null;
  }
  function syncStickies(ctx, block) {
    var ev = liveEv(ctx, block) || evaluate(ctx);
    if (!ev || !ev.item) return;
    if (ctx.subsOff() && ev.plan) enforceOneTime(ctx);
    var sub0 = !ctx.subsOff() && !!ev.plan;
    if (!sub0) { var dp = displayPack(ctx, block, ev); if (dp) ev = { item: ev.item, pack: dp, plan: ev.plan }; }
    var o = offerSummary(ctx, block, ev);
    var fx = !o.subscription ? packOneTime(ctx, ev.pack) : null;
    var pt = fx ? { total: fx.total, full: fx.full || fx.total } : poTotals(ctx, ev.pack, o.total, o.full, o.subscription);
    var total = pt.total, full = pt.full;
    var bar = null;
    each(block.querySelectorAll('.kaching-bundles__bar--selected'), function (b) { if (!bar && !closest(b, '.kaching-bundles__subscriptions, .kaching-bundles__subscriptions-wrapper')) bar = b; });
    var titleEl = bar && bar.querySelector('.kaching-bundles__bar-title');
    var title = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : (ev.pack ? ev.pack.title : '');
    var showCompare = !!(full && total && full !== total && moneyCents(full) !== ctx.singleCompare);
    var meta = o.bottles + (o.bottles > 1 ? ' bottles' : ' bottle') + ' · ' +
      (o.subscription ? (o.interval ? 'Delivered every ' + o.interval : (o.planName || 'Subscription')) : 'One-time delivery');
    var thumb = ev.pack && ev.pack.thumb;
    preorderSync(ctx, ev.pack);

    var scope = document;
    each(scope.querySelectorAll('[data-slimmy-k-total]'), function (el) { setPriceText(el, total); });
    each(scope.querySelectorAll('[data-slimmy-k-compare]'), function (el) { setPriceText(el, showCompare ? full : ''); if (el.hidden !== !showCompare) el.hidden = !showCompare; });
    each(scope.querySelectorAll('[data-slimmy-k-title]'), function (el) { if (title) el.textContent = title; });
    each(scope.querySelectorAll('[data-slimmy-k-meta]'), function (el) { el.textContent = meta; });
    if (thumb) each(scope.querySelectorAll('img[data-slimmy-k-thumb]'), function (el) { if (el.getAttribute('src') !== thumb) el.setAttribute('src', thumb); });
    // payment tiles: in subscribe mode hide wallets that cannot pay for subscriptions (data-sub-ok="false")
    each(scope.querySelectorAll('[data-slimmy-payicons]'), function (ul) {
      var vis = 0;
      each(ul.querySelectorAll('[data-slimmy-pay]'), function (li) { var h = o.subscription && li.getAttribute('data-sub-ok') === 'false'; li.hidden = h; if (!h) vis++; });
      ul.hidden = vis === 0;
    });
    // Shrine sticky ATC (button[form=…]) → "Add to cart · $79.98" (purchase-mode price); hide its single-bottle price
    ctx.externalTriggers().forEach(function (b) {
      if (b.querySelector('.sticky-atc__label__text') && total) {
        b._slimmyReadyLabel = (origLabel(b) || b.getAttribute('data-label') || 'Add to cart') + ' · ' + total;
        if (ctx.state === 'ready') setLabel(b, null);
      }
      each(b.querySelectorAll('[id^="sticky-atc-price-"]'), function (p) { p.setAttribute('data-slimmy-hide', 'single-price'); });
      var sticky = closest(b, 'sticky-atc');
      if (sticky) {
        var m = sticky.querySelector('[data-slimmy-k-sticky-meta]');
        if (!m) {
          var host = sticky.querySelector('.sticky-atc__left__content');
          if (host) { m = document.createElement('p'); m.className = 'slimmy-sticky-meta'; m.setAttribute('data-slimmy-k-sticky-meta', ''); host.appendChild(m); }
        }
        if (m) m.textContent = meta;
      }
    });
    syncSubPanel(ctx, o);
    decoratePanel(ctx, block, ev);
  }

  // Preorder (theme settings "Slimmy · Preorder"): button labels + "Estimated shipping" line follow the selected
  // pack's map flag "po" (STOQ preorder list + stock rules, computed in Liquid). Presentation only.
  function preorderSync(ctx, pack) {
    if (!pack || typeof pack.po !== 'boolean') return;
    var po = pack.po;
    each(document.querySelectorAll('[data-slimmy-po-label]'), function (el) {
      var want = po ? el.getAttribute('data-slimmy-po-label') : el.getAttribute('data-slimmy-ready-label');
      if (!want) return;
      if (el.hasAttribute('data-slimmy-kguard-text')) {
        if (el.getAttribute('data-slimmy-kguard-orig') !== want) el.setAttribute('data-slimmy-kguard-orig', want);
      } else if (el.textContent !== want) el.textContent = want;
    });
    each(document.querySelectorAll('[data-slimmy-preorder-ship]'), function (el) { if (el.hidden === po) el.hidden = !po; });
    poSaveSync(ctx, pack);
    if (ctx.state === 'ready' && !ctx.inflight) {
      if (ctx.button && !ctx.button._slimmyReadyLabel) setLabel(ctx.button, null);
      ctx.externalTriggers().forEach(function (b) { if (!b._slimmyReadyLabel) setLabel(b, null); });
    }
  }

  // "Preorder price: save $X · Ships by …" (snippet slimmy-preorder, part 'ship'; markup only while the preorder discount
  // setting is on, fixed mode). X = the selected pack's one-time $ off (pack map poOff.ot, same data as the repainted
  // prices). Text only: lines owned by another guarded form are left to that form's context; the "Pick your pack" line
  // (data-for="smp", not a guarded form) follows this context because its cards drive Kaching's selection.
  function poSaveSync(ctx, pack) {
    var c = poCfg(ctx), fid = ctx.formId();
    var off = c && c.mode === 'fixed' && pack && pack.po === true && pack.poOff ? Number(pack.poOff.ot) || 0 : 0;
    var pc = pack ? Number(pack.priceCents) || 0 : 0;
    var txt = '';
    if (off > 0 && (!pc || off < pc)) {
      txt = fmtLike(pack.price || '$0.00', off);
      if (off % 100 === 0) txt = txt.replace(/[.,]00(?!\d)/, '');
    }
    each(document.querySelectorAll('[data-slimmy-po-save]'), function (el) {
      var line = closest(el, '[data-slimmy-preorder-ship]'), owner = line && line.getAttribute('data-for');
      if (owner && fid && owner !== fid) {
        var of = document.getElementById(owner);
        if (of && of.matches && of.matches(FORM_SEL)) return;
      }
      var amt = el.querySelector('[data-slimmy-po-save-amt]');
      if (txt && amt && amt.textContent !== txt) amt.textContent = txt;
      var hide = !txt;
      if (el.hidden !== hide) el.hidden = hide;
    });
  }

  // Subscription panel (theme setting show_subscription_panel; markup only exists when ON)
  function collectFor(ctx, sel) {
    var fid = ctx.formId(), arr = [];
    each(ctx.form.querySelectorAll(sel), function (p) { arr.push(p); });
    if (fid) each(document.querySelectorAll(sel + '[data-for="' + fid + '"], [data-slimmy-afterbuy][data-for="' + fid + '"] ' + sel), function (p) { if (arr.indexOf(p) === -1) arr.push(p); });
    return arr;
  }
  // Subscribe tab currently selected in Kaching's purchase toggle (DOM state; used only to avoid collapsing the
  // disclosure for a frame while Kaching re-renders after a pack tap).
  function subTabSelected(ctx) {
    var b = ctx.block(); if (!b) return false;
    var sel = b.querySelector('.kaching-bundles__subscriptions .kaching-bundles__bar--selected');
    return !!sel && isSubscribeTab(sel) === true;
  }
  // Disclosure text: "Automatically renews every {interval} at {price}. Change or cancel anytime." (data-template)
  function paintSubPanel(ctx, interval, price, bottles, pack) {
    var panels = collectFor(ctx, '[data-slimmy-subpanel]');
    var po = poCfg(ctx), first = po && pack && pack.po === true && price ? poText(ctx, pack, price, true) : '';
    panels.forEach(function (p) {
      if (!interval || !price) { p.hidden = true; return; }
      var tpl = p.getAttribute('data-template') || 'Automatically renews every {interval} at {price}. Change or cancel anytime.';
      if (first && first !== price) tpl = po.subTemplate || 'First shipment {first}. Then {price} every {interval}. Change or cancel anytime.';
      var txt = tpl.replace('{qty}', bottles + (bottles > 1 ? ' bottles' : ' bottle'))
        .replace('{interval}', interval).replace('{first}', first).replace('{price}', price);
      var t = p.querySelector('[data-slimmy-sub-text]') || p;
      if (t.textContent !== txt) t.textContent = txt;
      p.hidden = false;
    });
  }
  function syncSubPanel(ctx, o) {
    var checks = collectFor(ctx, '[data-slimmy-subcheck]');
    if (ctx.subsOff()) { checks.forEach(function (c) { c.hidden = true; }); paintSubPanel(ctx, '', '', 0); return; }
    var sub = !!(o && o.subscription);
    // "Change or cancel easily": subscribe mode only, and only if the markup says the portal is verified
    // (Liquid renders it only when settings.portal_verified is ON).
    checks.forEach(function (c) { c.hidden = !(sub && c.getAttribute('data-verified') === 'true'); });
    var price = o && (o.renewPrice || o.planPrice || o.total);
    if (sub && o.interval && price) { paintSubPanel(ctx, o.interval, price, o.bottles, o.pack); return; }
    // Mid re-render (Subscribe still selected, plan not yet readable): keep the last line instead of collapsing it.
    if (subTabSelected(ctx) || (ctx._subIntentUntil && now() < ctx._subIntentUntil)) return;
    paintSubPanel(ctx, '', '', 0);
  }

  function each(list, fn) { for (var i = 0; i < list.length; i++) fn(list[i]); }

  /* ------------------------------------------------------------ watcher */
  function tick(ctx) {
    if (!ctx.form.isConnected) return;
    var r = evaluate(ctx);
    bindBlockEvents(ctx, r.block || ctx.block());
    if (r.ready) {
      setState(ctx, 'ready', r.why);
      return;
    }
    if (ctx.state === 'ready') {
      // was ready, Kaching is re-writing the form (offer change) → brief sync window
      ctx.deadline = now() + CFG.timeoutMs;
      setState(ctx, 'syncing', r.why);
    }
    if (r.fail) return fail(ctx, r.why);
    if (now() > ctx.deadline) return fail(ctx, 'timeout ' + CFG.timeoutMs + 'ms: ' + r.why);
    if (ctx.state === 'failed') return;
    log('waiting', ctx.kind, r.why);
  }
  function fail(ctx, why) {
    if (ctx.state !== 'failed') console.warn('[slimmy-kaching] purchase blocked — Kaching not ready:', why);
    setState(ctx, 'failed', why);
  }
  function schedule(ctx, ms) {
    clearTimeout(ctx._timers.tick);
    ctx._timers.tick = setTimeout(function () { tick(ctx); }, ms || 0);
  }
  function arm(ctx) {
    ctx.deadline = now() + CFG.timeoutMs;
    if (ctx.state !== 'ready') setState(ctx, 'loading', 'armed');
    clearInterval(ctx._timers.poll);
    // Light polling of the REAL signal (not a delay): stops on ready/failed.
    ctx._timers.poll = setInterval(function () {
      tick(ctx);
      if (ctx.state === 'ready' || ctx.state === 'failed') clearInterval(ctx._timers.poll);
    }, 400);
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined(BLOCK_TAG).then(function () { log('customElements.whenDefined(' + BLOCK_TAG + ') resolved'); schedule(ctx, 0); });
    }
    if (!ctx._mo) {
      ctx._mo = new MutationObserver(function () { schedule(ctx, 30); });
      ctx._mo.observe(ctx.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'data-initialized', 'class', 'name', 'checked', 'disabled'] });
      ctx.form.addEventListener('change', function () { schedule(ctx, 0); }, true); // e.g. selling_plan radios
    }
    tick(ctx);
  }
  // v7: paint totals immediately (optimistic) on Subscribe/One-time pointerdown from pack-map / selected bar DOM,
  // then confirm via liveEv + MutationObserver. Never changes Kaching selection — display only.
  function paintPack(ctx, pack) {
    var fx = packOneTime(ctx, pack);
    if (!fx) return false;
    writeTotals(ctx, fx.total, fx.full || fx.total);
    return true;
  }
  function paintTotals(ctx, total, full, pack, sub) {
    if (!total) return;
    var pt = poTotals(ctx, pack, total, full, sub);
    writeTotals(ctx, pt.total, pt.full);
  }
  function writeTotals(ctx, total, full) {
    if (!total) return;
    var showCompare = !!(full && total && full !== total && moneyCents(full) !== ctx.singleCompare);
    each(document.querySelectorAll('[data-slimmy-k-total]'), function (el) { setPriceText(el, total); });
    each(document.querySelectorAll('[data-slimmy-k-compare]'), function (el) {
      setPriceText(el, showCompare ? full : ''); if (el.hidden !== !showCompare) el.hidden = !showCompare;
    });
    ctx.externalTriggers().forEach(function (b) {
      if (b.querySelector('.sticky-atc__label__text') && total) {
        b._slimmyReadyLabel = (origLabel(b) || b.getAttribute('data-label') || 'Add to cart') + ' · ' + total;
        if (ctx.state === 'ready') setLabel(b, null);
      }
    });
  }
  function selectedDealBar(block) {
    var bar = null;
    each(block.querySelectorAll('.kaching-bundles__bar--selected'), function (b) {
      if (!bar && !closest(b, '.kaching-bundles__subscriptions, .kaching-bundles__subscriptions-wrapper')) bar = b;
    });
    return bar;
  }
  function isSubscribeTab(el) {
    if (!el) return false;
    var tab = closest(el, '.kaching-bundles__subscriptions .kaching-bundles__bar');
    if (!tab) return null;
    var wrap = closest(tab, '.kaching-bundles__subscriptions');
    if (!wrap) return null;
    var tabs = wrap.querySelectorAll(':scope > .kaching-bundles__bar, .kaching-bundles__bar');
    // Prefer semantic match; fall back to index (0 = one-time, 1 = subscribe) matching Slimmy Kaching config
    var t = (tab.textContent || '').toLowerCase();
    if (/subscribe/.test(t)) return true;
    if (/one[\s-]?time/.test(t)) return false;
    var idx = -1;
    for (var i = 0; i < tabs.length; i++) if (tabs[i] === tab) { idx = i; break; }
    return idx > 0;
  }
  function optimisticMode(ctx, block, e) {
    var sub = isSubscribeTab(e.target);
    if (sub === null) return false;
    if (sub && ctx.subsOff()) { setTimeout(function () { enforceOneTime(ctx); }, 0); return false; }
    var ev = liveEv(ctx, block) || evaluate(ctx);
    if (!ev || !ev.item) return false;
    var pack = ev.pack || ctx.pack(ev.item.id);
    var total = '', full = '';
    if (sub) {
      var planId = ev.plan;
      if (!planId && pack && pack.plans) {
        var keys = Object.keys(pack.plans);
        planId = keys.length ? keys[0] : '';
      }
      var plan = pack && pack.plans && pack.plans[planId];
      total = (plan && plan.price) || '';
      full = (pack && pack.price) || '';
      // Prefer a live price already rendered on the Subscribe tab if present
      var tab = closest(e.target, '.kaching-bundles__subscriptions .kaching-bundles__bar');
      var tabPrice = tab && tab.querySelector('.kaching-bundles__bar-price:not([data-slimmy-inj])');
      // Tab shows the CURRENT pack's subscribe price only after Kaching re-renders; pack-map is authoritative pre-paint
      if (!total && tabPrice) total = tabPrice.textContent.replace(/\s+/g, ' ').trim();
    } else {
      var bar = selectedDealBar(block);
      var bvid = bar ? barVariantId(block, bar, -1, ctx.packMap() || {}) : null;
      if (paintPack(ctx, (bvid && ctx.pack(bvid)) || pack)) { ctx._subIntentUntil = 0; paintSubPanel(ctx, '', '', 0); return true; }
      var priceEl = bar && bar.querySelector('.kaching-bundles__bar-price:not([data-slimmy-inj])');
      total = priceEl ? priceEl.textContent.replace(/\s+/g, ' ').trim() : ((pack && pack.price) || '');
      var fullEl = bar && bar.querySelector('.kaching-bundles__bar-full-price:not([data-slimmy-inj])');
      full = fullEl ? fullEl.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    if (!total) return false;
    paintTotals(ctx, total, full, pack, !!sub);
    if (sub) { ctx._subIntentUntil = now() + 800; if (plan && plan.price) paintSubPanel(ctx, intervalFrom(plan), plan.price, pack && pack.bottles || 1, pack); }
    else { ctx._subIntentUntil = 0; paintSubPanel(ctx, '', '', 0); }
    log('optimistic mode', sub ? 'subscribe' : 'one-time', total);
    return true;
  }
  function optimisticBar(ctx, block, e) {
    var lab = closest(e.target, '.kaching-bundles__bar[data-deal-bar-id] label, .kaching-bundles__bar[data-deal-bar-id]');
    if (!lab || closest(lab, '.kaching-bundles__subscriptions')) return false;
    var bar = closest(lab, '.kaching-bundles__bar[data-deal-bar-id]');
    if (!bar) return false;
    var priceEl = bar.querySelector('.kaching-bundles__bar-price:not([data-slimmy-inj])');
    var fullEl = bar.querySelector('.kaching-bundles__bar-full-price:not([data-slimmy-inj])');
    var total = priceEl ? priceEl.textContent.replace(/\s+/g, ' ').trim() : '';
    var full = fullEl ? fullEl.textContent.replace(/\s+/g, ' ').trim() : '';
    // If currently in subscribe mode, prefer pack-map plan price for the bar's variant
    var ev = liveEv(ctx, block);
    if (ev && ev.plan && !ctx.subsOff()) {
      var vid = barVariantId(block, bar, -1, ctx.packMap() || {});
      var pack = vid ? ctx.pack(vid) : null;
      var plan = pack && pack.plans && (pack.plans[ev.plan] || pack.plans[Object.keys(pack.plans)[0]]);
      if (plan && plan.price) { total = plan.price; full = (pack && pack.price) || full; paintSubPanel(ctx, intervalFrom(plan), plan.price, pack && pack.bottles || 1, pack); }
    }
    if (!pack) { var vid2 = barVariantId(block, bar, -1, ctx.packMap() || {}); pack = vid2 ? ctx.pack(vid2) : null; }
    if (!(ev && ev.plan && !ctx.subsOff()) && paintPack(ctx, pack)) return true; // v10: one-time → pack map only
    if (!total) return false;
    paintTotals(ctx, total, full, pack, !!(ev && ev.plan && !ctx.subsOff()));
    return true;
  }
  // v6/v7: same-frame price sync. MutationObserver + pointerdown optimistic paint.
  // Display only; the readiness/purchase logic (tick/evaluate) is unchanged.
  function fastSync(ctx) {
    var block = ctx.block();
    if (!block || !block.querySelector('.kaching-bundles__bar--selected')) return;
    var mo = ctx._fastMo;
    if (mo) mo.disconnect();
    try { syncStickies(ctx, block); } catch (e) { log('fastSync error', e); }
    if (mo) { mo.takeRecords(); observeFast(ctx, block); }
  }
  function observeFast(ctx, block) {
    ctx._fastMo.observe(block, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'checked', 'aria-checked', 'aria-selected'] });
  }
  function bindFast(ctx, block) {
    if (!window.MutationObserver) return;
    if (ctx._fastMo) ctx._fastMo.disconnect();
    ctx._fastMo = new MutationObserver(function () { fastSync(ctx); });
    observeFast(ctx, block);
    function afterMode() {
      if (window.queueMicrotask) queueMicrotask(function () { fastSync(ctx); });
      requestAnimationFrame(function () { fastSync(ctx); });
    }
    block.addEventListener('pointerdown', function (e) {
      optimisticMode(ctx, block, e);
      // v10: no bar pre-paint on pointerdown — the button must not lead the highlighted card (Kaching moves the
      // highlight on click; the click handler below + MutationObserver repaint in the same task).
    }, true);
    ['click', 'change', 'keyup'].forEach(function (t) {
      block.addEventListener(t, function (e) {
        if (t === 'click') { optimisticMode(ctx, block, e); optimisticBar(ctx, block, e); }
        afterMode();
      });
    });
  }
  function bindBlockEvents(ctx, block) {
    if (!block || ctx.boundBlock === block) return;
    ctx.boundBlock = block;
    bindFast(ctx, block);
    ['items-changed', 'deal-bar-selected', 'variants-changed', 'variant-selected'].forEach(function (name) {
      block.addEventListener(name, function (e) {
        log('kaching event', name, e.detail || '');
        fastSync(ctx);
        // Kaching's own listener writes the form synchronously; evaluate right after.
        schedule(ctx, 0);
        setTimeout(function () { tick(ctx); if (ctx.state === 'ready') cosmetics(ctx); }, 60);
      });
    });
  }
  function retry(ctx) {
    ctx.retries++;
    log('retry #' + ctx.retries, ctx.kind);
    var runtimeMissing = typeof window.__kachingBundlesInitializeInternal !== 'function' && !(window.customElements && customElements.get(BLOCK_TAG));
    if (ctx.retries >= 2 || runtimeMissing || /kaching-variant-mismatch/.test(ctx.reason || '')) { location.reload(); return; }
    ctx.state = 'loading';
    ctx.deadline = now() + 5000;           // short re-check, then reload on next Retry
    setState(ctx, 'loading', 'retry');
    arm(ctx);
    ctx.deadline = now() + 5000;
  }

  /* --------------------------------------------------------- in-flight */
  function startFlight(ctx, source) {
    ctx.inflight = true;
    ctx.inflightStarted = now();
    ctx.sawCartActivity = false;
    ctx._progClicks = 0;
    ctx._submits = 0;
    ctx._addErr = null; // v12
    log('in-flight start via', source);
    render(ctx);
    clearTimeout(ctx._timers.safety); clearTimeout(ctx._timers.idle);
    ctx._timers.safety = setTimeout(function () { endFlight(ctx, 'safety timeout'); }, CFG.inflightMaxMs);
    ctx._timers.idle = setTimeout(function () {
      var btnBusy = ctx.button && (ctx.button.classList.contains('loading') || ctx.button.disabled);
      if (!ctx.sawCartActivity && !btnBusy) endFlight(ctx, 'no cart activity (Kaching aborted?)');
    }, CFG.noActivityMs);
  }
  function endFlight(ctx, why) {
    if (!ctx.inflight) return;
    ctx.inflight = false;
    clearTimeout(ctx._timers.safety); clearTimeout(ctx._timers.idle);
    if (ctx.sawCartActivity || ctx._submits) ctx.cooldownUntil = now() + CFG.cooldownMs; // v11
    log('in-flight end:', why);
    var home = ctx.form.querySelector('input[name="return_to"][data-slimmy-return]');
    if (home) home.value = '';
    tick(ctx);
    render(ctx);
  }
  function watchFlightSignals(ctx) {
    // Shrine product-form adds .loading to the submit button during fetch and removes it in finally{}
    if (ctx.button) {
      new MutationObserver(function () {
        var loading = ctx.button.classList.contains('loading');
        if (loading) { ctx.sawCartActivity = true; ctx._sawLoading = true; }
        else if (ctx._sawLoading && ctx.inflight) { ctx._sawLoading = false; setTimeout(function () { endFlight(ctx, 'product-form finished'); }, 250); }
      }).observe(ctx.button, { attributes: true, attributeFilter: ['class'] });
    }
  }
  function watchGlobalCartActivity() {
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (en) {
          if (/\/cart\/add/.test(en.name)) contexts.forEach(function (c) { if (c.inflight) { c.sawCartActivity = true; log('cart request seen', en.name); } });
        });
      }).observe({ type: 'resource', buffered: false });
    } catch (e) {}
    try {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          contexts.forEach(function (c) { if (c.inflight) setTimeout(function () { endFlight(c, 'cartUpdate pub/sub'); }, 250); });
        });
      }
    } catch (e) {}
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) contexts.forEach(function (c) { endFlight(c, 'pageshow (bfcache)'); arm(c); });
    });
  }

  /* ------------------------------------------- preorder add via fetch (v12) */
  var ADD_ACTION_RE = /\/cart\/add(\.js)?\/?(?:$|[?#])/;
  function poAjaxWanted(ctx, fd) {
    if (!ctx.subsOff()) return false;
    var vid = fd.get('id');
    var pack = vid ? ctx.pack(vid) : null;
    return !!(pack && pack.po === true);
  }
  function stoqHijackExpected() {
    var cfg = window._RestockRocketConfig;
    if (!cfg || !cfg.integrations || !cfg.integrations.some) return false;
    return cfg.integrations.some(function (i) {
      return i && i.type === 'hijack' && i.enabled && i.page_types && i.page_types.indexOf(cfg.pageType) !== -1 &&
        i.configuration && i.configuration.enableFetchHijack;
    });
  }
  function waitFor(test, maxMs) {
    return new Promise(function (res) {
      if (test()) return res(true);
      var t0 = now(), iv = setInterval(function () {
        if (test()) { clearInterval(iv); res(true); } else if (now() - t0 >= maxMs) { clearInterval(iv); res(false); }
      }, 50);
    });
  }
  // Resolves when a /cart/update request started after t0 has completed (STOQ's cart-attribute flush), or after maxMs.
  function waitCartUpdate(t0, maxMs) {
    return new Promise(function (res) {
      var done = false, po = null, timer = null;
      function fin(v) { if (done) return; done = true; clearTimeout(timer); try { if (po) po.disconnect(); } catch (e) {} res(v); }
      function hit(en) { return /\/cart\/update(\.js)?(\?|#|$)/.test(String(en.name || '')) && en.startTime >= t0; }
      try { if (performance.getEntriesByType('resource').some(hit)) return fin(true); } catch (e) {}
      try { po = new PerformanceObserver(function (l) { if (l.getEntries().some(hit)) fin(true); }); po.observe({ type: 'resource', buffered: true }); } catch (e) {}
      timer = setTimeout(function () { fin(false); }, maxMs);
    });
  }
  function withTimeout(p, ms) {
    return Promise.race([Promise.resolve(p).catch(function () { return null; }), new Promise(function (r) { setTimeout(function () { r(null); }, ms); })]);
  }
  function addReturnTo(fd) {
    var r = fd.get('return_to');
    if (typeof r === 'string' && r) {
      try { var u = new URL(r, location.href); if (u.origin === location.origin) return u.pathname + u.search + u.hash; } catch (e) {}
    }
    return ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart';
  }
  function showAddError(ctx, msg) {
    var st = ensureStatus(ctx);
    each(st.querySelectorAll('[data-slimmy-kguard-retry], .slimmy-kguard-fallback'), function (el) { el.hidden = true; });
    ctx._addErr = { msg: msg, until: now() + 10000 };
    render(ctx);
  }
  function poAjaxAdd(ctx, form, fd) {
    ctx.sawCartActivity = true; // the add is under way: keep the flight (and the duplicate-tap block) alive
    ctx._poAjax = true;
    var rr = function () { return window._RestockRocket || {}; };
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var armed = stoqHijackExpected() ? waitFor(function () { return !!rr().hijackArmedAt; }, 2000) : Promise.resolve(false);
    armed.then(function (wasArmed) {
      log('preorder add via fetch (STOQ hijack ' + (wasArmed ? 'armed' : 'not armed') + ')', fd.get('id'));
      var t0 = performance.now();
      return fetch(root + 'cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (resp) {
          return resp.json().catch(function () { return null; }).then(function (j) {
            if (!resp.ok) {
              ctx._poAjax = false;
              endFlight(ctx, 'preorder add failed ' + resp.status);
              showAddError(ctx, (j && (j.description || j.message)) || 'Could not add to cart');
              return;
            }
            var line = j && (j.items ? j.items[0] : j);
            var tagged = !!(line && line.properties && line.properties._stoq_shopify_selling_plan_id);
            log('preorder add ok, STOQ properties ' + (tagged ? 'present' : 'missing'));
            var after = tagged ? waitCartUpdate(t0, 1500)
              : (typeof rr().updateCartSellingPlans === 'function' ? withTimeout(rr().updateCartSellingPlans(), 2500) : Promise.resolve());
            return after.then(function () { location.href = addReturnTo(fd); });
          });
        });
    }).catch(function (err) {
      ctx._poAjax = false;
      if (err && err.name === 'CartRequestCancelledError') { endFlight(ctx, 'preorder add cancelled'); return; }
      log('preorder add via fetch failed, submitting natively', err);
      try { HTMLFormElement.prototype.submit.call(form); } catch (e) { endFlight(ctx, 'native fallback failed'); }
    });
  }
  // Bubble phase on window: runs after every other submit handler; only takes submits nobody else handled.
  function onSubmitLate(e) {
    if (e.defaultPrevented) return;
    var form = e.target, ctx = ctxForForm(form);
    if (!ctx || !ctx.inflight) return;
    var sub = e.submitter || null;
    var action = (sub && sub.getAttribute && sub.getAttribute('formaction')) || form.getAttribute('action') || '';
    if (!ADD_ACTION_RE.test(action)) return;
    var fd;
    try { fd = sub ? new FormData(form, sub) : new FormData(form); }
    catch (x) { fd = new FormData(form); if (sub && sub.name) fd.append(sub.name, sub.value || ''); }
    if (!poAjaxWanted(ctx, fd)) return;
    e.preventDefault();
    poAjaxAdd(ctx, form, fd);
  }

  /* ---------------------- Kaching selection helpers + self-heal (v14) */
  // Kaching's live selected deal bar id (Solid prop / attribute), not the DOM selected class.
  function kachingSelectedBarId(block) {
    if (!block) return null;
    var id = null;
    try { id = block.selectedDealBarId; } catch (e) {}
    if (id == null || id === '') {
      try { id = block.getAttribute && block.getAttribute('selected-deal-bar-id'); } catch (e2) {}
    }
    return id == null || id === '' ? null : String(id);
  }
  // True when THIS bar is the deal Kaching currently has selected (would add / would deselect on re-tap).
  function isKachingSelectedBar(block, bar, form) {
    if (!bar) return false;
    var barId = bar.getAttribute('data-deal-bar-id');
    if (!barId) return false;
    var kid = kachingSelectedBarId(block);
    if (kid) return kid === String(barId);
    var vid = null;
    try {
      var items = block && typeof block.items === 'function' ? block.items() : null;
      if (items && items[0] && items[0].id != null) vid = String(items[0].id);
    } catch (e) {}
    if (!vid && form) {
      var inp = form.querySelector('[name="id"]');
      if (inp && inp.value) vid = String(inp.value);
    }
    if (vid) {
      var barVid = barDefaultVariant(block, bar);
      if (barVid) return String(barVid) === vid;
    }
    return bar.classList.contains('kaching-bundles__bar--selected');
  }
  // Re-select the highlighted card so Kaching catches up to the last tap (no Offers unavailable).
  function maybeHealMismatch(ctx, block, selBar) {
    if (!selBar || !block || ctx.inflight || ctx._healing) return;
    var t = now();
    if (ctx._healAt && t - ctx._healAt < CFG.healGapMs) return;
    if ((ctx._healCount || 0) >= CFG.healMax) return;
    if (isKachingSelectedBar(block, selBar, ctx.form)) return;
    var barId = selBar.getAttribute('data-deal-bar-id');
    ctx._healAt = t;
    ctx._healCount = (ctx._healCount || 0) + 1;
    log('self-heal mismatch: re-select highlighted bar', barId, 'heal#', ctx._healCount);
    ctx._healing = true;
    try {
      var radio = selBar.querySelector('input[type="radio"]');
      var main = selBar.querySelector('.kaching-bundles__bar-main');
      if (radio && !radio.checked) radio.click();
      else if (main) main.click();
      else if (radio) { try { radio.checked = false; } catch (e) {} radio.click(); }
    } catch (e) { log('self-heal error', e); }
    setTimeout(function () { ctx._healing = false; schedule(ctx, 0); }, 0);
  }

  /* ------------------------------------- selected card re-tap (v13/v14) */
  function onSelectedBarClick(e) {
    if (!contexts.length) return;
    var main = closest(e.target, '.kaching-bundles__bar-main');
    if (!main) return;
    var bar = closest(main, '.kaching-bundles__bar[data-deal-bar-id]');
    if (!bar || closest(bar, '.kaching-bundles__subscriptions')) return;
    var inner = closest(e.target, 'button, a, select, input, textarea, label, [role="listbox"], [role="combobox"], [role="option"]');
    if (inner && main.contains(inner) && inner !== main) return; // a control inside the card keeps working
    for (var i = 0; i < contexts.length; i++) {
      var c = contexts[i], blk = null;
      try { blk = c.block(); } catch (x) {}
      if (!(c.form.contains(bar) || (blk && blk.contains(bar)))) continue;
      if (c._healing) return; // v14: never swallow the self-heal click
      // v14: Kaching's own selected deal — not the DOM selected class (can lag during fast taps)
      if (!isKachingSelectedBar(blk, bar, c.form)) return;
      stop(e);
      log('kept selected card on re-tap (Kaching would deselect it to the first bar)', bar.getAttribute('data-deal-bar-id'));
      return;
    }
  }

  /* ----------------------------------------------------- interception */
  function ctxForForm(form) {
    for (var i = 0; i < contexts.length; i++) if (contexts[i].form === form) return contexts[i];
    return null;
  }
  // Map a click target to {ctx, role}
  function classify(target) {
    var el = closest(target, 'button, input[type="submit"], a, [role="button"], shopify-accelerated-checkout, shopify-buy-it-now-button, .shopify-payment-button');
    if (!el) return null;
    for (var i = 0; i < contexts.length; i++) {
      var c = contexts[i];
      if (c.button && (el === c.button || c.button.contains(el))) return { ctx: c, role: 'main', el: c.button };
      if (closest(el, '.slimmy-kguard-status')) return null; // Retry etc.
      var ext = c.externalTriggers();
      for (var j = 0; j < ext.length; j++) if (ext[j] === el || ext[j].contains(el)) return { ctx: c, role: 'external', el: ext[j] };
      if (c.form.contains(el) && closest(el, 'shopify-accelerated-checkout, shopify-buy-it-now-button, .shopify-payment-button')) return { ctx: c, role: 'dynamic', el: el };
      if (closest(el, 'kaching-bundles-sticky-atc')) return { ctx: c, role: 'kaching-sticky', el: el };
    }
    return null;
  }

  function onClick(e) {
    if (!contexts.length) return;
    var hit = classify(e.target);
    if (!hit) return;
    var ctx = hit.ctx;

    // Programmatic re-click by Kaching (or our own routing) during the accepted flight.
    // v11: our routing click is exempt; otherwise only ONE programmatic click per flight (Kaching's follow-up).
    if (!e.isTrusted && ctx.inflight && hit.role === 'main') {
      if (ctx._routing) { log('allow routed click (in flight)'); return; }
      if (ctx._progClicks < 1) { ctx._progClicks++; log('allow programmatic click (in flight)'); return; }
      stop(e); log('BLOCKED extra programmatic click (in flight)'); return;
    }
    // v11: post-add cooldown (covers the gap between /cart/add returning and the unlock, and fast re-taps).
    if (!ctx.inflight && now() < ctx.cooldownUntil) { stop(e); log('BLOCKED', hit.role, 'click (post-add cooldown)'); return; }

    var r = evaluate(ctx);
    if (!r.ready) {
      stop(e);
      log('BLOCKED', hit.role, 'click — not ready:', r.why);
      if (ctx.state === 'ready') setState(ctx, 'syncing', r.why);
      if (ctx.state !== 'failed') { if (now() > ctx.deadline) ctx.deadline = now() + CFG.timeoutMs; schedule(ctx, 0); }
      if (hit.role !== 'main') scrollToWidget(ctx);
      return;
    }
    if (ctx.state !== 'ready') setState(ctx, 'ready', r.why);
    if (ctx.inflight) { stop(e); log('BLOCKED duplicate', hit.role, 'click (in flight)'); return; }
    if (ctx.originallyUnavailable) { stop(e); return; }

    if (ctx.subsOff()) disableSubPlanControls(ctx);
    if (hit.role === 'main' || hit.role === 'dynamic' || hit.role === 'kaching-sticky') {
      startFlight(ctx, hit.role);
      return; // let Kaching's own handler on the button proceed
    }
    // External trigger (sticky bars): never submit directly — route through the Kaching button.
    stop(e);
    var ret = ctx.form.querySelector('input[name="return_to"][data-slimmy-return]');
    if (ret && hit.el.getAttribute('data-slimmy-return-to')) ret.value = hit.el.getAttribute('data-slimmy-return-to');
    startFlight(ctx, 'external→main');
    log('routing external trigger through Kaching button');
    ctx._routing = true;
    try { ctx.button.click(); } finally { ctx._routing = false; }
  }

  function onSubmit(e) {
    var ctx = ctxForForm(e.target);
    if (!ctx) return;
    // v11: one submit per in-flight add; none during the post-add cooldown.
    if (ctx.inflight && ctx._submits >= 1) { stop(e); log('BLOCKED duplicate submit (in flight)'); return; }
    if (!ctx.inflight && now() < ctx.cooldownUntil) { stop(e); log('BLOCKED submit (post-add cooldown)'); return; }
    var r = evaluate(ctx);
    if (!r.ready) { stop(e); log('BLOCKED submit — not ready:', r.why); schedule(ctx, 0); return; }
    if (ctx.subsOff()) disableSubPlanControls(ctx);
    if (!ctx.inflight) startFlight(ctx, 'submit');
    ctx._submits++;
  }

  function scrollToWidget(ctx) {
    var target = (ctx.state === 'failed' && ctx.statusEl) || ctx.placeholder() || ctx.form;
    try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { target.scrollIntoView(); }
  }

  /* ------------------------------------------------------ sticky bars */
  function setupSticky(ctx) {
    if (ctx._stickySetup || !('IntersectionObserver' in window)) return;
    var fid = ctx.formId(), list = [];
    function add(el) { if (el && list.indexOf(el) === -1) list.push(el); }
    if (fid) each(document.querySelectorAll('[data-slimmy-sticky-for="' + fid + '"]'), add);
    ctx.externalTriggers().forEach(function (b) { add(closest(b, 'sticky-atc, [data-sm-sticky]')); });
    if (!list.length || !ctx.button) return;
    ctx._stickySetup = true;
    // fixed bars must not live under a transformed/contained ancestor → move to <body>
    list.forEach(function (el) { if (el.hasAttribute('data-slimmy-sticky-portal') && el.parentNode !== document.body) document.body.appendChild(el); });
    var offer = closest(ctx.form, '[data-slimmy-offer-root], #offer') || ctx.form;
    var st = { offerIn: true, atcPast: false };
    function measure() {
      var h = 0;
      list.forEach(function (s) {
        var cs = getComputedStyle(s), b = parseFloat(cs.bottom) || 0;
        if (cs.display === 'none') return;
        h = Math.max(h, s.offsetHeight + (cs.position === 'fixed' ? b : 0));
      });
      if (h) document.documentElement.style.setProperty('--slimmy-sticky-h', Math.ceil(h) + 'px');
    }
    function apply() {
      var show = st.atcPast && !st.offerIn;
      list.forEach(function (s) { s.setAttribute('data-slimmy-sticky', show ? 'show' : 'hide'); });
      // body gets bottom padding = bar height only while a bar is visible (never covers the footer / last content)
      if (show) { measure(); document.documentElement.setAttribute('data-slimmy-sticky-on', ''); }
      else document.documentElement.removeAttribute('data-slimmy-sticky-on');
      log('sticky', show ? 'show' : 'hide', st);
    }
    function initial() {
      var vh = window.innerHeight, o = offer.getBoundingClientRect(), a = ctx.button.getBoundingClientRect();
      st.offerIn = o.bottom > 0 && o.top < vh;
      st.atcPast = a.bottom < 0;
      apply();
    }
    new IntersectionObserver(function (es) {
      es.forEach(function (e) { st.offerIn = e.isIntersecting; }); apply();
    }, { threshold: 0 }).observe(offer);
    new IntersectionObserver(function (es) {
      es.forEach(function (e) { st.atcPast = !e.isIntersecting && e.boundingClientRect.bottom < (e.rootBounds ? e.rootBounds.top : 0) + 1; }); apply();
    }, { threshold: 0 }).observe(ctx.button);
    document.documentElement.classList.add('slimmy-has-sticky');
    measure(); initial();
    window.addEventListener('resize', function () { measure(); }, { passive: true });
    // IO only fires on intersection *changes*; a jump scroll (anchor, Home/End, scrollTo) can skip the button
    // entirely, so re-check geometry on scroll too (rAF-throttled, read-only).
    var rafPending = false;
    window.addEventListener('scroll', function () {
      if (rafPending) return; rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        var vh = window.innerHeight, o = offer.getBoundingClientRect(), a = ctx.button.getBoundingClientRect();
        var oi = o.bottom > 0 && o.top < vh, ap = a.bottom < 0;
        if (oi !== st.offerIn || ap !== st.atcPast) { st.offerIn = oi; st.atcPast = ap; apply(); }
      });
    }, { passive: true });
  }

  /* ---------------------------------------------------------------- boot */
  // v10: drop a stale ?variant= once (see header "Root cause guarded"). Address bar only; Kaching's selection,
  // the form and the cart are untouched. Skipped in the theme editor.
  var urlCleaned = false;
  function stripVariantParam() {
    if (urlCleaned) return;
    urlCleaned = true;
    try {
      if (window.Shopify && window.Shopify.designMode) return;
      var u = new URL(location.href);
      if (!u.searchParams.has('variant')) return;
      u.searchParams.delete('variant');
      var q = u.searchParams.toString();
      history.replaceState(history.state, '', u.pathname + (q ? '?' + q : '') + u.hash);
      log('removed ?variant= from the URL (Kaching native-variant remap guard)');
    } catch (e) {}
  }
  function scan() {
    var forms = document.querySelectorAll(FORM_SEL);
    for (var i = 0; i < forms.length; i++) {
      if (ctxForForm(forms[i])) continue;
      var ctx = new Ctx(forms[i]);
      if (!ctx.button) { log('guarded form without submit button', forms[i]); }
      contexts.push(ctx);
      if (ctx.subsOff()) { var slot = ctx.form.querySelector('[data-slimmy-kaching-slot]'); if (slot && !slot.classList.contains('slimmy-subs-off')) slot.classList.add('slimmy-subs-off'); }
      log('guarding form', ctx.kind, ctx.formId() || forms[i]);
      watchFlightSignals(ctx);
      setupSticky(ctx);
      arm(ctx);
    }
    for (var k = contexts.length - 1; k >= 0; k--) if (!contexts[k].form.isConnected) contexts.splice(k, 1); // in place
    if (contexts.length) stripVariantParam();
    installSubsOffNetGuard();
  }

  document.addEventListener('click', onSelectedBarClick, true); // v13/v14: before Kaching's delegated bar onclick
  document.addEventListener('click', onClick, true);   // capture: runs before Kaching's button listener
  document.addEventListener('submit', onSubmit, true);
  window.addEventListener('submit', onSubmitLate, false); // v12
  window.addEventListener('kaching-bundles-initialized', function () {
    log('event kaching-bundles-initialized (window.kachingBundlesInitialized=' + window.kachingBundlesInitialized + ')');
    contexts.forEach(function (c) { c.initSeenAt = now(); schedule(c, 0); setTimeout(function () { tick(c); }, 1600); });
  });
  document.addEventListener('shopify:section:load', function () { setTimeout(scan, 50); });
  document.documentElement.classList.add('slimmy-kguard-on');
  watchGlobalCartActivity();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();

  window.SlimmyKachingGuard = {
    contexts: contexts,
    evaluateAll: function () { return contexts.map(function (c) { var r = evaluate(c); return { kind: c.kind, state: c.state, inflight: c.inflight, ready: r.ready, why: r.why }; }); },
    debug: function (on) { DEBUG = on !== false; try { localStorage.setItem('slimmyKachingDebug', DEBUG ? '1' : '0'); } catch (e) {} return DEBUG; },
    rescan: scan,
    // v11: true while an add is in flight or in its post-add cooldown (optionally only for one form)
    busy: function (form) { var t = now(); return contexts.some(function (c) { return (!form || c.form === form) && (c.inflight || t < c.cooldownUntil); }); },
    subsOff: function () { return contexts.map(function (c) { return { kind: c.kind, subsOff: c.subsOff(), plans: c._subPlanIds }; }); }
  };
})();
